import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { useCurrentInternalUser, isAdmin } from '../lib/currentUser';
import Sidebar from '../components/Sidebar';
import HowItWorksButton from '../components/HowItWorksButton';
import StarIcon from '../components/StarIcon';
import { Otto, OttoBriefingBar } from '../components/otto';
import { gazeHover } from '../components/otto/ottoGaze';
import OttoGallery from '../components/otto/OttoGallery';
import AppFooter from '../components/AppFooter';
import { isTestEnv } from '../lib/isTestEnv';
import { DASHBOARD_EMPTY_COPY } from '../components/turbo';
import { SaveStatus, useAutosave } from '../components/saveStatus';
import { useKpiCountUp } from '../components/kpiCountUp';
import InboxZeroCard from '../components/InboxZeroCard';
import { fmtDate, fmtDateCompact } from '../lib/dates';
import { deriveFeedbackDue } from '../lib/briefFeedback';
import AddButton from '../components/AddButton';


// Widget registry. The dashboard's bottom 2:1 grid is composed of
// these widgets — left column gets the wide ones, right column gets
// the narrow ones. Each widget is keyed by a stable id so we can
// store hidden ids on the user row without coupling to render order.
//
// New widgets default to visible: persistence stores a `hidden` array
// (not `visible`), so shipping a new widget doesn't require
// backfilling every internal_users row.
// Calendar lives in its own slot beside the welcome banner / stats
// rather than in the bottom widget grid, so it acts as a persistent
// "at-a-glance" reference at the top of the page. Still toggleable
// via Manage Widgets like the rest.
const WIDGETS_TOP_IDS   = ['calendar'];
const WIDGETS_LEFT_IDS  = ['recent_activity', 'pinned_suppliers', 'pinned_briefs', 'briefs_due_this_week', 'at_risk'];
const WIDGETS_RIGHT_IDS = ['my_notes'];

const WIDGET_LABELS = {
  recent_activity:      'Recent Activity',
  briefs_due_this_week: 'Briefs Due This Week',
  at_risk:              'At Risk',
  pinned_suppliers:     'Pinned Suppliers',
  pinned_briefs:        'Pinned Briefs',
  calendar:             'Calendar',
  my_notes:             'My Notes',
};

// Reserved for widgets that should be gated to admin/super_user. The
// dashboard no longer ships any — Compliance Gaps moved to the
// Suppliers > Compliance Overview page so there's one source of
// truth. Keep the guard scaffolding in case a future admin-only
// widget arrives.
const ADMIN_ONLY_WIDGETS = new Set();

// At-risk threshold: a brief is at risk if response_count is less than
// AT_RISK_RESPONSE_FLOOR_PCT of recipient_count AND the deadline lands
// within AT_RISK_DEADLINE_DAYS. Both knobs live here so we can tune
// without hunting through the render path.
const AT_RISK_RESPONSE_FLOOR_PCT = 0.3;
const AT_RISK_DEADLINE_DAYS = 7;

// Recent Activity feed length. Server-side capping happens via per-
// source .limit() calls; the merged + sorted list is trimmed to _MAX for
// storage, and _LIMIT items show by default — "View all" expands to the
// full stored set.
const RECENT_ACTIVITY_LIMIT = 10;
const RECENT_ACTIVITY_MAX = 50;


// Tiny sparkline used beneath each stat card. SVG polyline scaled to
// fill its container; vector-effect keeps the stroke crisp at any
// width. Renders the line over a 0.08-opacity polygon fill so the
// curve casts a faint green shadow beneath itself — matches the
// dashboard_refined.html reference and makes the line read at a
// glance even on the dark panel. Renders nothing when there isn't
// enough data to draw a line so freshly-installed projects don't
// show a flat zero artefact.
//
// Min / max endpoints + per-point hover values are wired so the
// sparkline carries readable signal rather than just gesturing at
// shape. The optional `labels` prop labels each bucket on hover
// ("3 weeks ago", "This week", …); if omitted, the tooltip just shows
// the value. Min point is shown as a faint outlined ring, max as a
// solid filled dot.
const Sparkline = ({ data, color = '#92D400', animate = false, labels }) => {
  const polylineRef = useRef(null);
  const [pathLength, setPathLength] = useState(0);
  // When `animate` is true, the path renders with stroke-dashoffset
  // equal to its full length on first paint, then a CSS transition
  // animates the offset to 0 (left-to-right draw-in). When false, it
  // renders fully drawn from frame one.
  const [drawn, setDrawn] = useState(!animate);
  const dataKey = Array.isArray(data) ? data.join(',') : '';

  useEffect(() => {
    if (!polylineRef.current) return undefined;
    const len = polylineRef.current.getTotalLength();
    setPathLength(len);
    if (!animate) {
      setDrawn(true);
      return undefined;
    }
    // Two-RAF trick — gives the browser one frame to commit the
    // initial dash-offset = length state, then we flip drawn so the
    // CSS transition has something to interpolate between.
    setDrawn(false);
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setDrawn(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [dataKey, animate]);

  if (!Array.isArray(data) || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const width = 100;
  const height = 24;
  const xStep = width / (data.length - 1);
  // Per-point coordinates so we can scatter the hit-targets + min/max
  // dots at the exact line positions. Avoids re-deriving them inline
  // every time we need to reference a point.
  const points = data.map((v, i) => ({
    v,
    x: i * xStep,
    y: height - (v / max) * height,
  }));
  const linePoints = points.map(p => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');
  // Fill polygon: same line points, plus the bottom-right and
  // bottom-left corners so the shape closes against the baseline.
  const fillPoints = `0,${height} ${linePoints} ${width},${height}`;
  // Find one min + one max index. Tiebreak: prefer the most-recent
  // (rightmost) occurrence so the dot tracks the latest movement.
  let minIdx = 0;
  let maxIdx = 0;
  for (let i = 1; i < data.length; i++) {
    if (data[i] <= data[minIdx]) minIdx = i;
    if (data[i] >= data[maxIdx]) maxIdx = i;
  }
  // Suppress endpoint dots when the data is flat (every point equal)
  // so they don't appear as redundant decoration on stretches with no
  // movement. ALSO suppress the min dot when it's a trailing zero —
  // a zero-count current week is common and the dot otherwise reads
  // as a loose floating mark below the visible curve (post-review).
  const hasRange = data.some(v => v !== data[0]);
  const minDotSuppressed = minIdx === data.length - 1 && data[minIdx] === 0;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="w-full h-6 mt-2 overflow-visible">
      <polygon
        points={fillPoints}
        fill={color}
        opacity="0.12"
        stroke="none" />
      <polyline
        ref={polylineRef}
        points={linePoints}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        className="kpi-spark-path"
        style={pathLength ? {
          strokeDasharray: pathLength,
          strokeDashoffset: drawn ? 0 : pathLength,
        } : undefined} />
      {hasRange && (
        <>
          {/* Min point — small outlined ring, low contrast so it sits
              behind the line rather than competing with it. Skipped
              when it would be a trailing zero (see minDotSuppressed). */}
          {!minDotSuppressed && (
            <circle cx={points[minIdx].x} cy={points[minIdx].y} r="1.6" fill="#131313" stroke={color} strokeWidth="1" vectorEffect="non-scaling-stroke" />
          )}
          {/* Max point — solid filled dot, higher contrast. */}
          <circle cx={points[maxIdx].x} cy={points[maxIdx].y} r="1.8" fill={color} stroke="none" />
        </>
      )}
      {/* Hover hit-targets — invisible circles with a native <title>
          tooltip per point. Browser handles the popover so we don't
          need positioning JS. r is wide enough that any hover near
          the line catches the nearest point. */}
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="3.5" fill="transparent" stroke="none">
          <title>{labels && labels[i] ? `${labels[i]}: ${p.v}` : `${p.v}`}</title>
        </circle>
      ))}
    </svg>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user: currentInternalUser } = useCurrentInternalUser();
  const [stats, setStats] = useState({ suppliers: 0, briefs: 0, responses: 0 });
  // thisMonthSuppliers retained for the dropped Total Suppliers KPI tile;
  // suppress unused warning since the loader still populates it (cheap)
  // and we may want it back for a Reporting drill-in.
  // eslint-disable-next-line no-unused-vars
  const [thisMonthSuppliers, setThisMonthSuppliers] = useState(0);
  // Awarded MTD + prev-month for the 4th KPI tile (2026-06 mock).
  // Sum of awarded_amount on briefs closed within the current calendar
  // month, and the same metric for the prior month so the tile can
  // surface a "vs May" % delta. Both default to 0 so the tile renders
  // a clean £0 on a fresh install rather than skeleton state.
  const [awardedMtd, setAwardedMtd] = useState(0);
  const [awardedPrevMonth, setAwardedPrevMonth] = useState(0);
  const [pinnedSuppliers, setPinnedSuppliers] = useState([]);
  const [pinnedBriefs, setPinnedBriefs] = useState([]);
  const [briefsDueThisWeek, setBriefsDueThisWeek] = useState([]);
  // Counts that gate the 100% empty "Inbox Zero" card. All five must
  // read zero for the card to render. Initialised null so the card
  // doesn't flash before the first fetch completes — we only consider
  // the supplier "inbox zero" once we've actually checked.
  // Post-event feedback outstanding — count + oldest age, for the 3+ Otto alert
  // (brief-briefs-tab-row §7). Derived from the shared helper so it matches the
  // Briefs "Feedback due" queue exactly.
  const [feedbackDue, setFeedbackDue] = useState({ count: 0, oldestDays: null });
  const [inboxZeroCounts, setInboxZeroCounts] = useState({
    pendingResponses: null,
    overdueBriefs: null,
    amberSuppliers: null,
    redSuppliers: null,
    notSetSuppliers: null,
  });
  // Local override of the user's hidden-widget list. We can't write
  // directly into the hook's user object, so toggles update this
  // state alongside persisting to internal_users.dashboard_widgets.
  // Falls back to whatever the hook last reported when null.
  const [hiddenWidgetsOverride, setHiddenWidgetsOverride] = useState(null);
  // My Notes textarea is locally-controlled and autosaved via the
  // shared useAutosave hook (wired further down — saveNotes references
  // currentUserId which isn't declared until then).
  const [notes, setNotes] = useState('');
  const notesSeededRef = useRef(false);
  const [manageOpen, setManageOpen] = useState(false);
  // Otto intro banner — shown every login. The user can dismiss it for
  // the current session (sessionStorage), but it returns on their next
  // login (Login.js clears the flag on sign-in).
  const [showIntro, setShowIntro] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return sessionStorage.getItem('otto_intro_dismissed') !== '1'; } catch (e) { return true; }
  });
  const dismissIntro = () => {
    setShowIntro(false);
    try { sessionStorage.setItem('otto_intro_dismissed', '1'); } catch (e) { /* ignore */ }
  };
  const manageWrapperRef = useRef(null);
  // Calendar widget displays a month grid for at-a-glance date
  // reference. Defaults to the current month; prev/next buttons page
  // through. Stored as a Date pinned to the 1st so month maths stays
  // simple. Days with at least one brief due (filtered by role like
  // the Briefs Due This Week widget) get a small green dot beneath
  // the day number so the calendar earns its place rather than just
  // being decorative.
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  // Sparkline trends — six weekly buckets of "rows created" for each
  // stat card. Buckets[0] is the oldest week, buckets[5] is the
  // current week, so the line reads left-to-right oldest → newest.
  // Same shape across all three stats for visual consistency.
  const [trends, setTrends] = useState({ suppliers: [], briefs: [], responses: [] });
  // Week-ago snapshot counts for the KPI delta read. Drives the true
  // "vs last week" delta — current count minus this snapshot — so the
  // headline number and the delta tell a coherent story.
  // kpiWeekAgo snapshot retained so a future "vs last week" delta can
  // come back without re-adding the count queries — currently unread
  // since the 2026-06 corrections #7 switched KPI tiles to a
  // "new this week" delta sourced from the trends array instead.
  // eslint-disable-next-line no-unused-vars
  const [kpiWeekAgo, setKpiWeekAgo] = useState({ briefs: 0, responses: 0 });
  // Flips true after the first stats fetch lands. Gates the KPI
  // count-up so the animation runs from 0 → real value, never 0 → 0.
  const [statsLoaded, setStatsLoaded] = useState(false);
  // KPI count-up orchestration. Starts "done" only when the user
  // prefers reduced motion — in that case deltas and sparklines
  // render in their final state immediately. Otherwise stays false
  // until the count-up + 200ms beat completes. Fires on every
  // Dashboard mount (e.g., navigating back to /dashboard from
  // /briefs) since the state resets when the route unmounts.
  const [kpiAnimationDone, setKpiAnimationDone] = useState(() => {
    if (typeof window === 'undefined') return true;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return true;
    return false;
  });
  // Scope toggle — 'mine' (default) or 'everyone'. Admin/super_user
  // only; regular users never see the toggle and always get 'mine'.
  // Seeded from internal_users.dashboard_scope once the user record
  // arrives so the choice persists across sessions and devices.
  // Dashboard view scope now lives in the sidebar ContextSelector; the
  // dashboard just reads the persisted preference off currentInternalUser
  // (re-derives on each refetch the sidebar triggers via notify).
  const scope = currentInternalUser?.dashboard_scope === 'everyone' ? 'everyone' : 'mine';
  // Real Recent Activity feed — replaces the old "coming soon"
  // placeholder. Pulled from multiple tables (brief_send_log,
  // brief_responses, briefs awards, compliance_audit_log, suppliers)
  // and merged client-side. Scope-aware: 'mine' filters to events on
  // briefs/suppliers the user owns or touched; 'everyone' returns the
  // raw feed.
  const [recentActivity, setRecentActivity] = useState([]);
  // "View all" expands the feed from RECENT_ACTIVITY_LIMIT to the full
  // stored set (up to RECENT_ACTIVITY_MAX) inline.
  const [activityExpanded, setActivityExpanded] = useState(false);
  // At-risk briefs — sent briefs the user owns whose deadline lands
  // within AT_RISK_DEADLINE_DAYS AND response_count / recipient_count
  // < AT_RISK_RESPONSE_FLOOR_PCT. Scope-aware: 'mine' restricts to
  // owned briefs; 'everyone' opens to the company.
  const [atRiskBriefs, setAtRiskBriefs] = useState([]);

  // Cross-reference sets — a brief can legitimately surface in BOTH
  // panels (At Risk = low-traction lens, Due This Week = deadline-soon
  // lens). Per the dashboard review brief §3 we surface that overlap
  // with a subtle "also at risk" / "also due this week" chip on the
  // row so the reader doesn't suspect a duplicate. The Sets stay tiny
  // (at most a handful of rows on either panel) so this is cheap.
  const atRiskBriefIdSet = useMemo(() => new Set(atRiskBriefs.map(b => b.id)), [atRiskBriefs]);
  const briefsDueThisWeekIdSet = useMemo(() => new Set(briefsDueThisWeek.map(b => b.id)), [briefsDueThisWeek]);

  const userIsAdmin = isAdmin(currentInternalUser);
  const currentUserId = currentInternalUser?.id || null;
  // Effective scope respects the role gate: non-admins always see
  // 'mine', even if their saved value is something else (e.g. role
  // demoted after a previous admin save). The toggle UI also hides
  // for non-admins.
  const effectiveScope = userIsAdmin ? scope : 'mine';

  // My Notes autosave wiring — placed after currentUserId so the
  // useCallback dep array doesn't read it from the temporal dead zone.
  const saveNotes = useCallback(async (value) => {
    if (!currentUserId) return;
    const { error } = await supabase
      .from('internal_users')
      .update({ notes: value })
      .eq('id', currentUserId);
    if (error) throw new Error(error.message);
  }, [currentUserId]);
  const notesStatus = useAutosave(notes, saveNotes, {
    enabled: notesSeededRef.current && !!currentUserId,
  });

  // KPI count-up: one hook per tile. enabled gates on statsLoaded so
  // we never animate from 0 → placeholder 0; the hook also bails
  // internally for reduced-motion / session-already-animated cases.
  // suppliersCount retained behind eslint-disable for the same reason
  // as thisMonthSuppliers above — hook stays warm in case Suppliers
  // returns to the dashboard as a tile later.
  // eslint-disable-next-line no-unused-vars
  const suppliersCount = useKpiCountUp(stats.suppliers, { enabled: statsLoaded });
  const briefsCount = useKpiCountUp(stats.briefs, { enabled: statsLoaded });
  const responsesCount = useKpiCountUp(stats.responses, { enabled: statsLoaded });

  // 200ms beat after the 750ms count-up before the delta arrows fade
  // in. Skipped when kpiAnimationDone is already true (reduced
  // motion) so deltas render statically in that case.
  useEffect(() => {
    if (!statsLoaded || kpiAnimationDone) return undefined;
    const timer = setTimeout(() => setKpiAnimationDone(true), 950);
    return () => clearTimeout(timer);
  }, [statsLoaded, kpiAnimationDone]);

  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    const getStats = async () => {
      // Total Suppliers is always company-wide — there's one supplier
      // base, scope doesn't change that.
      const { count: supplierCount } = await supabase.from('suppliers').select('*', { count: 'exact', head: true });

      // Active Briefs + Responses are scope-aware. When 'mine' we
      // restrict to briefs the user owns (owner_id or brief_owners
      // junction). For Responses we pull is_submitted rows joined to
      // their brief and filter client-side; the response volume is
      // small enough that one fetch + a filter is cheaper than a
      // server-side OR across the join.
      let briefCount = 0;
      let responseCount = 0;
      if (effectiveScope === 'everyone') {
        const [briefsRes, responsesRes] = await Promise.all([
          supabase.from('briefs').select('*', { count: 'exact', head: true }).eq('status', 'sent').is('archived_at', null),
          supabase.from('brief_responses').select('*', { count: 'exact', head: true }).eq('is_submitted', true),
        ]);
        briefCount = briefsRes.count || 0;
        responseCount = responsesRes.count || 0;
      } else {
        // Active briefs owned by the user — same definition as
        // BriefReporting "my briefs".
        const { data: briefsData } = await supabase
          .from('briefs')
          .select('id, owner_id, brief_owners(user_id)')
          .eq('status', 'sent')
          .is('archived_at', null);
        const myBriefIds = (briefsData || [])
          .filter(b => b.owner_id === currentUserId || (b.brief_owners || []).some(bo => bo.user_id === currentUserId))
          .map(b => b.id);
        briefCount = myBriefIds.length;
        if (myBriefIds.length > 0) {
          const { count } = await supabase
            .from('brief_responses')
            .select('*', { count: 'exact', head: true })
            .eq('is_submitted', true)
            .in('brief_id', myBriefIds);
          responseCount = count || 0;
        }
      }

      if (cancelled) return;
      setStats({ suppliers: supplierCount || 0, briefs: briefCount, responses: responseCount });
      setStatsLoaded(true);

      // Banner sub-line counts — suppliers + briefs created since the
      // first of the current calendar month. Two count-only HEAD queries
      // (head: true) so we don't ship every row over the wire just to
      // count them.
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);
      const startISO = startOfMonth.toISOString();
      const startOfPrevMonth = new Date(startOfMonth);
      startOfPrevMonth.setMonth(startOfPrevMonth.getMonth() - 1);
      const startPrevISO = startOfPrevMonth.toISOString();
      const [monthSuppliersRes, awardedMtdRes, awardedPrevRes] = await Promise.all([
        supabase.from('suppliers').select('*', { count: 'exact', head: true }).gte('created_at', startISO),
        // Awarded month-to-date — sum of awarded_amount on closed briefs
        // closed within the current calendar month. select() instead of
        // a count-head so we can sum client-side (Supabase JS doesn't
        // expose SQL SUM via the REST surface). Awarded sums are small
        // enough that pulling the rows over the wire is fine.
        supabase.from('briefs').select('awarded_amount').eq('status', 'closed').gte('closed_at', startISO).not('awarded_amount', 'is', null),
        supabase.from('briefs').select('awarded_amount').eq('status', 'closed').gte('closed_at', startPrevISO).lt('closed_at', startISO).not('awarded_amount', 'is', null),
      ]);
      if (cancelled) return;
      setThisMonthSuppliers(monthSuppliersRes.count || 0);
      const sumAmounts = (rows) => (rows || []).reduce((s, r) => s + (Number(r.awarded_amount) || 0), 0);
      setAwardedMtd(sumAmounts(awardedMtdRes.data));
      setAwardedPrevMonth(sumAmounts(awardedPrevRes.data));
    };
    getStats();
    return () => { cancelled = true; };
  }, [currentUserId, effectiveScope]);

  // Inbox Zero counts. Five conditions that all need to read zero
  // before the InboxZeroCard renders above the bottom widget grid:
  //   · pendingResponses: brief_recipients still awaiting a supplier
  //     response on an active brief
  //   · overdueBriefs: active briefs whose required_response_time has
  //     passed (waiting on action regardless of recipient state)
  //   · amberSuppliers / redSuppliers / notSetSuppliers: active
  //     suppliers in those compliance buckets
  // Always company-wide, not scope-filtered — the card reads "all
  // work is done" rather than "my work is done", so it doesn't make
  // sense to fire it while colleagues still have outstanding items.
  useEffect(() => {
    let cancelled = false;
    const loadInboxZero = async () => {
      const nowIso = new Date().toISOString();
      const [pendingRes, overdueRes, complianceRes, questionsRes, updateReqRes] = await Promise.all([
        supabase
          .from('brief_recipients')
          .select('briefs!inner(status)', { count: 'exact', head: true })
          .eq('status', 'pending')
          .eq('briefs.status', 'sent'),
        supabase
          .from('briefs')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'sent')
          .is('archived_at', null)
          .lt('required_response_time', nowIso),
        supabase
          .from('suppliers')
          .select('compliance_status')
          .eq('status', 'active'),
        // Unanswered supplier questions on live briefs (supplier-questions §8).
        supabase
          .from('brief_questions')
          .select('briefs!inner(status)', { count: 'exact', head: true })
          .eq('status', 'open')
          .eq('briefs.status', 'sent'),
        // Outstanding post-deadline update requests on live briefs.
        supabase
          .from('brief_update_requests')
          .select('briefs!inner(status)', { count: 'exact', head: true })
          .is('resolved_at', null)
          .eq('briefs.status', 'sent'),
      ]);
      if (cancelled) return;
      const rows = complianceRes.data || [];
      const amber = rows.filter(r => r.compliance_status === 'amber').length;
      const red = rows.filter(r => r.compliance_status === 'red').length;
      const notSet = rows.filter(r => !r.compliance_status).length;
      setInboxZeroCounts({
        pendingResponses: pendingRes.count || 0,
        overdueBriefs: overdueRes.count || 0,
        amberSuppliers: amber,
        redSuppliers: red,
        notSetSuppliers: notSet,
        unansweredQuestions: questionsRes.count || 0,
        updateRequests: updateReqRes.count || 0,
      });
    };
    loadInboxZero();
    return () => { cancelled = true; };
  }, [currentUserId]);

  // Feedback outstanding across awarded briefs — for the 3+ dashboard alert.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: awarded } = await supabase.from('briefs')
        .select('id, status, closed_at, awarded_supplier_id, awarded_amount, event_live_date, event_live_end_date')
        .eq('status', 'closed').not('awarded_supplier_id', 'is', null);
      if (cancelled) return;
      if (!awarded?.length) { setFeedbackDue({ count: 0, oldestDays: null }); return; }
      const { data: fb } = await supabase.from('brief_feedback')
        .select('brief_id, supplier_id, status, completed_at').in('brief_id', awarded.map(b => b.id));
      if (cancelled) return;
      const map = {};
      for (const row of (fb || [])) map[row.brief_id] = row;
      const due = deriveFeedbackDue(awarded, map, { thresholdGBP: 0 }); // oldest event first
      setFeedbackDue({ count: due.length, oldestDays: due.length ? due[0].daysSinceEvent : null });
    })();
    return () => { cancelled = true; };
  }, [currentUserId]);

  // Derived once-per-render: all five counts strictly zero. Null
  // counts (the initial state before fetch completes) keep this
  // false so the card doesn't flash on mount.
  const isInboxZero =
    inboxZeroCounts.pendingResponses === 0 &&
    inboxZeroCounts.overdueBriefs === 0 &&
    inboxZeroCounts.amberSuppliers === 0 &&
    inboxZeroCounts.redSuppliers === 0 &&
    inboxZeroCounts.notSetSuppliers === 0 &&
    (inboxZeroCounts.unansweredQuestions || 0) === 0 &&
    (inboxZeroCounts.updateRequests || 0) === 0;

  // Pinned suppliers + briefs are now per-user via the user_pinned_*
  // join tables. Order by the foreign supplier_name / brief_name so the
  // panel reads alphabetically (same as the old global query), capped at
  // 5 each.
  useEffect(() => {
    if (!currentInternalUser?.id) return;
    let cancelled = false;
    (async () => {
      // Respect the user's drag-ordering on /pinned — sort_order asc,
      // unranked rows fall to the end. Same ordering as the Pinned page
      // so the dashboard panels read in lockstep.
      // Two-step join (pin rows → resolve by id) — see comment in
      // Pinned.js for why we don't use PostgREST embedded selects here.
      // select('*') keeps the queries resilient to schema variation
      // (e.g. sort_order column not yet added in some environments).
      const [pinSupplierRes, pinBriefRes] = await Promise.all([
        supabase
          .from('user_pinned_suppliers')
          .select('*')
          .eq('user_id', currentInternalUser.id)
          .order('pinned_at', { ascending: false })
          .limit(5),
        supabase
          .from('user_pinned_briefs')
          .select('*')
          .eq('user_id', currentInternalUser.id)
          .order('pinned_at', { ascending: false })
          .limit(5),
      ]);
      const bySortOrder = (a, b) => {
        const aS = a.sort_order ?? Number.POSITIVE_INFINITY;
        const bS = b.sort_order ?? Number.POSITIVE_INFINITY;
        if (aS !== bS) return aS - bS;
        return new Date(b.pinned_at || 0) - new Date(a.pinned_at || 0);
      };
      const supplierIds = (pinSupplierRes.data || []).slice().sort(bySortOrder).map(r => r.supplier_id);
      const briefIds    = (pinBriefRes.data    || []).slice().sort(bySortOrder).map(r => r.brief_id);

      const [suppliersRes, briefsRes] = await Promise.all([
        supplierIds.length > 0
          ? supabase
              .from('suppliers')
              .select('id, supplier_name, category, region, preferred_supplier, tier')
              .in('id', supplierIds)
          : Promise.resolve({ data: [] }),
        briefIds.length > 0
          ? supabase
              .from('briefs')
              .select('id, brief_name, brief_category, fusion_project_name, status, archived_at, required_response_time')
              .in('id', briefIds)
          : Promise.resolve({ data: [] }),
      ]);
      const supplierMap = new Map((suppliersRes.data || []).map(s => [s.id, s]));
      const briefMap    = new Map((briefsRes.data    || []).map(b => [b.id, b]));

      if (cancelled) return;
      setPinnedSuppliers(supplierIds.map(id => supplierMap.get(id)).filter(Boolean));
      setPinnedBriefs(briefIds.map(id => briefMap.get(id)).filter(Boolean));
    })();
    return () => { cancelled = true; };
  }, [currentInternalUser?.id]);

  // Briefs Due This Week. 7-day window on required_response_time,
  // status='sent', not archived. Scope-aware:
  //   'everyone' → all briefs in the window
  //   'mine' → only briefs the user owns (primary owner_id OR member
  //            of brief_owners — matches the "my briefs" definition
  //            in BriefReporting.js so the two views stay consistent).
  // The fetch pulls both joins and the filter runs client-side; the
  // window is small enough that this is cheaper than an OR across a
  // joined table.
  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    (async () => {
      const todayMid = new Date(); todayMid.setHours(0, 0, 0, 0);
      const sevenDaysOut = new Date(todayMid); sevenDaysOut.setDate(sevenDaysOut.getDate() + 7);
      const { data } = await supabase
        .from('briefs')
        .select('id, brief_name, fusion_project_name, brief_category, required_response_time, owner_id, brief_owners(user_id)')
        .eq('status', 'sent')
        .is('archived_at', null)
        .gte('required_response_time', todayMid.toISOString())
        .lte('required_response_time', sevenDaysOut.toISOString())
        .order('required_response_time', { ascending: true });
      if (cancelled) return;
      const rows = data || [];
      const scoped = effectiveScope === 'everyone'
        ? rows
        : rows.filter(b =>
            b.owner_id === currentUserId ||
            (b.brief_owners || []).some(bo => bo.user_id === currentUserId)
          );
      setBriefsDueThisWeek(scoped.slice(0, 8));
    })();
    return () => { cancelled = true; };
  }, [currentUserId, effectiveScope]);

  // Calendar event data — brief response deadlines only. Event live
  // dates used to render as a second (teal) dot but were removed:
  // their cadence is less actionable from the dashboard and the
  // doubled-up legend muddied the at-a-glance read. Briefs Due This
  // Week is the dashboard's single calendar signal.
  const [calendarEvents, setCalendarEvents] = useState([]);
  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    (async () => {
      const year = calendarMonth.getFullYear();
      const month = calendarMonth.getMonth();
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 1, 0, 23, 59, 59);
      const { data: dueRows } = await supabase
        .from('briefs')
        .select('id, brief_name, required_response_time, owner_id, brief_owners(user_id)')
        .eq('status', 'sent')
        .is('archived_at', null)
        .gte('required_response_time', start.toISOString())
        .lte('required_response_time', end.toISOString());
      if (cancelled) return;
      const filterByScope = (rows) => effectiveScope === 'everyone'
        ? rows
        : rows.filter(b =>
            b.owner_id === currentUserId ||
            (b.brief_owners || []).some(bo => bo.user_id === currentUserId)
          );
      const dueEvents = filterByScope(dueRows || []).map(b => ({
        day: new Date(b.required_response_time).getDate(),
        title: `${b.brief_name || 'Untitled brief'} brief due`,
        briefId: b.id,
      }));
      setCalendarEvents(dueEvents);
    })();
    return () => { cancelled = true; };
  }, [currentUserId, effectiveScope, calendarMonth]);

  // At Risk — briefs whose deadline is within AT_RISK_DEADLINE_DAYS
  // AND whose response_count is below AT_RISK_RESPONSE_FLOOR_PCT of
  // their recipient_count. Scope-aware. Pulls the upcoming-window
  // briefs along with their recipients + submitted responses in one
  // round-trip via PostgREST embeds, then computes ratios + filters
  // client-side. The list is short (< a dozen briefs in any given
  // week typically) so the volume is fine.
  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    (async () => {
      const todayMid = new Date(); todayMid.setHours(0, 0, 0, 0);
      const horizon = new Date(todayMid); horizon.setDate(horizon.getDate() + AT_RISK_DEADLINE_DAYS);
      const { data } = await supabase
        .from('briefs')
        .select('id, brief_name, fusion_project_name, brief_category, required_response_time, owner_id, brief_owners(user_id), brief_recipients(supplier_id, status)')
        .eq('status', 'sent')
        .is('archived_at', null)
        .gte('required_response_time', todayMid.toISOString())
        .lte('required_response_time', horizon.toISOString());
      if (cancelled) return;
      const rows = data || [];
      const scoped = effectiveScope === 'everyone'
        ? rows
        : rows.filter(b =>
            b.owner_id === currentUserId ||
            (b.brief_owners || []).some(bo => bo.user_id === currentUserId)
          );
      // A brief recipient with status='responded' (the legacy enum
      // value for "submitted") counts as a submitted response. We
      // count from brief_recipients rather than brief_responses so we
      // pick up both submitted-and-then-withdrawn and currently-
      // submitted in one go, matching the team's mental model of
      // "how many of the invited suppliers have replied?".
      const enriched = scoped.map(b => {
        const recipients = b.brief_recipients || [];
        const recipientCount = recipients.length;
        const respondedCount = recipients.filter(r => r.status === 'responded').length;
        const ratio = recipientCount === 0 ? 0 : respondedCount / recipientCount;
        return { ...b, recipientCount, respondedCount, ratio };
      });
      const atRisk = enriched
        .filter(b => b.recipientCount > 0 && b.ratio < AT_RISK_RESPONSE_FLOOR_PCT)
        .sort((a, b) => new Date(a.required_response_time) - new Date(b.required_response_time))
        .slice(0, 6);
      setAtRiskBriefs(atRisk);
    })();
    return () => { cancelled = true; };
  }, [currentUserId, effectiveScope]);

  // Recent Activity — last 5 events across briefs and suppliers,
  // surfaced from the various source tables. Each source returns at
  // most LIMIT*2 rows; we merge, sort by timestamp desc, then trim
  // to RECENT_ACTIVITY_LIMIT for display. Scope-aware:
  //   'everyone' → unfiltered merge
  //   'mine'     → keep only events tied to briefs the user owns or
  //                events the user performed (actor_id match)
  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    (async () => {
      const FETCH = RECENT_ACTIVITY_MAX;
      // Pull each source independently. Errors fail-soft — one bad
      // table doesn't blank the feed.
      const [sendLogRes, responseRes, awardRes, complianceRes, supplierRes, mentionRes, ownerAddRes, extensionReqRes, declinedRes, acceptedRes, badgeRes, statusChangeRes, amendRes, questionAskedRes, updateReqRes, statusUpdateRes] = await Promise.all([
        supabase
          .from('brief_send_log')
          .select('id, sent_at, sent_by, kind, brief_id, supplier_id, briefs(brief_name, owner_id, brief_owners(user_id)), internal_users!sent_by(full_name), suppliers(supplier_name)')
          .order('sent_at', { ascending: false })
          .limit(FETCH),
        supabase
          .from('brief_responses')
          .select('id, submitted_at, brief_id, supplier_id, is_submitted, briefs(brief_name, owner_id, brief_owners(user_id)), suppliers(supplier_name)')
          .eq('is_submitted', true)
          .not('submitted_at', 'is', null)
          .order('submitted_at', { ascending: false })
          .limit(FETCH),
        supabase
          .from('briefs')
          .select('id, brief_name, closed_at, closed_by, awarded_supplier_id, awarded_amount, owner_id, brief_owners(user_id), internal_users!closed_by(full_name), awarded_supplier:suppliers!awarded_supplier_id(supplier_name)')
          .eq('status', 'closed')
          .not('awarded_supplier_id', 'is', null)
          .not('closed_at', 'is', null)
          .order('closed_at', { ascending: false })
          .limit(FETCH),
        supabase
          .from('compliance_audit_log')
          .select('id, ts, action, actor_id, supplier_id, document_key, meta, internal_users!actor_id(full_name), suppliers(supplier_name)')
          .in('action', ['approved', 'reminder_sent', 'rejected', 'needs_clarification'])
          .order('ts', { ascending: false })
          .limit(FETCH),
        supabase
          .from('suppliers')
          .select('id, supplier_name, created_at')
          .order('created_at', { ascending: false })
          .limit(FETCH),
        // Brief-comment mentions OF the current user — "X mentioned you on
        // Brief". Personal by nature, so scoped to currentUserId here
        // (not in the scope filter) and surfaced in both 'mine' and
        // 'everyone'. suppressed rows (self / inactive / no-email) never
        // reached the user, so they're excluded.
        supabase
          .from('brief_comment_mention_notifications')
          .select('id, sent_at, brief_id, briefs(brief_name, owner_id), comment:brief_comments(author_id, author:internal_users!author_id(full_name))')
          .eq('mentioned_user_id', currentUserId)
          .is('suppressed_reason', null)
          .not('sent_at', 'is', null)
          .order('sent_at', { ascending: false })
          .limit(FETCH),
        // Briefs the current user was ADDED to as an owner — "X added you
        // to Brief". created_at distinguishes new additions from the
        // legacy backfill (NULL on pre-migration rows, filtered below).
        // No internal_users embed here: brief_owners was created
        // out-of-band and added_by may have no declared FK, which would
        // make an embed throw and blank the whole query. Actor names are
        // resolved in a follow-up fetch below instead.
        supabase
          .from('brief_owners')
          .select('brief_id, user_id, added_by, created_at, briefs(brief_name, owner_id)')
          .eq('user_id', currentUserId)
          .not('created_at', 'is', null)
          .order('created_at', { ascending: false })
          .limit(FETCH),
        // Supplier-requested deadline extensions — "{name} requested more
        // time on {brief}". Outstanding requests only (the flag is cleared
        // when Fusion grants an extension), surfaced to the brief's owners.
        supabase
          .from('brief_recipients')
          .select('id, extension_requested_at, brief_id, supplier_id, briefs(brief_name, owner_id, brief_owners(user_id)), suppliers(supplier_name)')
          .not('extension_requested_at', 'is', null)
          .order('extension_requested_at', { ascending: false })
          .limit(FETCH),
        // Supplier declined a brief — "{name} declined {brief}".
        supabase
          .from('brief_recipients')
          .select('id, declined_at, brief_id, supplier_id, briefs(brief_name, owner_id, brief_owners(user_id)), suppliers(supplier_name)')
          .eq('status', 'declined')
          .not('declined_at', 'is', null)
          .order('declined_at', { ascending: false })
          .limit(FETCH),
        // Supplier accepted a brief — "{name} accepted {brief}".
        supabase
          .from('brief_recipients')
          .select('id, accepted_at, brief_id, supplier_id, briefs(brief_name, owner_id, brief_owners(user_id)), suppliers(supplier_name)')
          .eq('status', 'accepted')
          .not('accepted_at', 'is', null)
          .order('accepted_at', { ascending: false })
          .limit(FETCH),
        // Recognition / Eco badge earned — "{name} reached Oak Status" (eco)
        // or "{name} earned {tier} {category}". awarded_at marks when the
        // current tier was reached (only bumped on a real tier change).
        supabase
          .from('supplier_badges')
          .select('id, tier, awarded_at, supplier_id, suppliers(supplier_name), badge_categories(slug, name, bronze_name, silver_name, gold_name)')
          .not('awarded_at', 'is', null)
          .order('awarded_at', { ascending: false })
          .limit(FETCH),
        // Supplier compliance traffic-light changed — "{supplier}'s compliance
        // changed to Red". Logged on every real transition (auto via the DB
        // recompute → changed_by NULL, or a manual override → the actor).
        // Skip first-time/seed rows (old_status NULL) so the one-off history
        // backfill doesn't flood the feed.
        supabase
          .from('supplier_compliance_status_history')
          .select('id, changed_at, old_status, new_status, changed_by, supplier_id, suppliers(supplier_name), internal_users!changed_by(full_name)')
          .not('old_status', 'is', null)
          .order('changed_at', { ascending: false })
          .limit(FETCH),
        // Brief amended — "{teammate} amended {brief}". One row per published
        // amendment in brief_versions (version_number > 1; v1 is the original
        // backfill). Silent saves are quiet internal corrections — excluded.
        // No internal_users embed: created_by may have no declared FK (an
        // embed would throw and blank the query), so names are resolved below.
        supabase
          .from('brief_versions')
          .select('id, created_at, created_by, brief_id, briefs(brief_name, owner_id, brief_owners(user_id))')
          .gt('version_number', 1)
          .eq('silent', false)
          .order('created_at', { ascending: false })
          .limit(FETCH),
        // "{supplier} asked a question on {brief}" — supplier-initiated, routed
        // to the brief's owners.
        supabase
          .from('brief_questions')
          .select('id, created_at, brief_id, suppliers(supplier_name), briefs(brief_name, owner_id, brief_owners(user_id))')
          .order('created_at', { ascending: false })
          .limit(FETCH),
        // "{supplier} asked for an update on {brief}" — post-deadline chase.
        supabase
          .from('brief_update_requests')
          .select('id, created_at, brief_id, suppliers(supplier_name), briefs(brief_name, owner_id, brief_owners(user_id))')
          .order('created_at', { ascending: false })
          .limit(FETCH),
        // "{teammate} sent a status update on {brief}" — holding update.
        supabase
          .from('brief_status_updates')
          .select('id, created_at, author_user_id, decision_by, brief_id, internal_users!author_user_id(full_name), briefs(brief_name, owner_id, brief_owners(user_id))')
          .order('created_at', { ascending: false })
          .limit(FETCH),
      ]);
      if (cancelled) return;

      // Resolve actor → name for events whose actor FK isn't embeddable
      // (owner-adds via brief_owners.added_by, amendments via
      // brief_versions.created_by — see notes above). One fetch for both.
      const ownerAddRows = (ownerAddRes.data || []).filter(r => r.added_by && r.added_by !== r.user_id);
      const amendRows = (amendRes.data || []);
      const actorIds = Array.from(new Set([
        ...ownerAddRows.map(r => r.added_by),
        ...amendRows.map(r => r.created_by).filter(Boolean),
      ]));
      let adderNameById = new Map();
      if (actorIds.length > 0) {
        const { data: adderRows } = await supabase
          .from('internal_users')
          .select('id, full_name')
          .in('id', actorIds);
        if (cancelled) return;
        adderNameById = new Map((adderRows || []).map(u => [u.id, u.full_name]));
      }

      const events = [];
      // brief_send_log → "You sent X to N suppliers" / "{name} sent X" plus
      // granted deadline extensions ("{name} extended the deadline on X").
      const sentByBriefBucket = new Map();
      const extendByBriefBucket = new Map();
      for (const row of sendLogRes.data || []) {
        // Group by brief + actor + same minute window so a fan-out (one
        // click → many supplier rows) collapses into a single event.
        const tsKey = row.sent_at ? new Date(row.sent_at).toISOString().slice(0, 16) : '';
        if (row.kind === 'deadline_extended') {
          // Granted extension — complements the supplier's "requested more
          // time" event. Keep the supplier name only when it's a single
          // grant; a fan-out collapses to "for N suppliers".
          const key = `${row.brief_id}::${row.sent_by}::${tsKey}`;
          const existing = extendByBriefBucket.get(key);
          if (existing) { existing.count += 1; existing.supplierName = null; continue; }
          extendByBriefBucket.set(key, {
            at: row.sent_at,
            actorId: row.sent_by,
            actorName: row.internal_users?.full_name,
            briefId: row.brief_id,
            briefName: row.briefs?.brief_name,
            briefOwnerId: row.briefs?.owner_id,
            briefCoOwners: (row.briefs?.brief_owners || []).map(bo => bo.user_id),
            supplierName: row.suppliers?.supplier_name,
            count: 1,
          });
          continue;
        }
        const key = `${row.brief_id}::${row.sent_by}::${tsKey}::${row.kind}`;
        const existing = sentByBriefBucket.get(key);
        if (existing) { existing.count += 1; continue; }
        sentByBriefBucket.set(key, {
          kind: row.kind === 'resend' ? 'resend' : 'sent',
          at: row.sent_at,
          actorId: row.sent_by,
          actorName: row.internal_users?.full_name,
          briefId: row.brief_id,
          briefName: row.briefs?.brief_name,
          briefOwnerId: row.briefs?.owner_id,
          briefCoOwners: (row.briefs?.brief_owners || []).map(bo => bo.user_id),
          count: 1,
        });
      }
      for (const e of sentByBriefBucket.values()) events.push({ type: 'sent', ...e });
      for (const e of extendByBriefBucket.values()) events.push({ type: 'extension_granted', ...e });

      for (const row of responseRes.data || []) {
        events.push({
          type: 'response',
          at: row.submitted_at,
          briefId: row.brief_id,
          briefName: row.briefs?.brief_name,
          supplierId: row.supplier_id,
          supplierName: row.suppliers?.supplier_name,
          briefOwnerId: row.briefs?.owner_id,
          briefCoOwners: (row.briefs?.brief_owners || []).map(bo => bo.user_id),
        });
      }

      for (const row of awardRes.data || []) {
        events.push({
          type: 'award',
          at: row.closed_at,
          actorId: row.closed_by,
          actorName: row.internal_users?.full_name,
          briefId: row.id,
          briefName: row.brief_name,
          supplierId: row.awarded_supplier_id,
          supplierName: row.awarded_supplier?.supplier_name,
          briefOwnerId: row.owner_id,
          briefCoOwners: (row.brief_owners || []).map(bo => bo.user_id),
          amount: row.awarded_amount,
        });
      }

      for (const row of complianceRes.data || []) {
        const cType = row.action === 'approved' ? 'approved'
          : row.action === 'reminder_sent' ? 'reminder'
          : row.action === 'rejected' ? 'compliance_rejected'
          : 'compliance_info';
        events.push({
          type: cType,
          at: row.ts,
          actorId: row.actor_id,
          actorName: row.internal_users?.full_name,
          supplierId: row.supplier_id,
          supplierName: row.suppliers?.supplier_name,
          documentKey: row.document_key,
        });
      }

      for (const row of supplierRes.data || []) {
        events.push({
          type: 'supplier_joined',
          at: row.created_at,
          supplierId: row.id,
          supplierName: row.supplier_name,
        });
      }

      // "X mentioned you on Brief" — already scoped to the current user
      // by the query. forMe flags it through the scope filter unchanged.
      for (const row of mentionRes.data || []) {
        events.push({
          type: 'mention',
          at: row.sent_at,
          actorId: row.comment?.author_id,
          actorName: row.comment?.author?.full_name,
          briefId: row.brief_id,
          briefName: row.briefs?.brief_name,
          forMe: true,
        });
      }

      // "X added you to Brief" — also pre-scoped to the current user.
      // ownerAddRows has self-adds (brief creators auto-mirror into
      // brief_owners) already filtered out, so "You added yourself" never
      // shows.
      for (const row of ownerAddRows) {
        events.push({
          type: 'added',
          at: row.created_at,
          actorId: row.added_by,
          actorName: adderNameById.get(row.added_by),
          briefId: row.brief_id,
          briefName: row.briefs?.brief_name,
          forMe: true,
        });
      }

      // "{name} requested more time on {brief}" — supplier-initiated, so no
      // actor; routed to the brief's owners via the scope filter.
      for (const row of extensionReqRes.data || []) {
        events.push({
          type: 'extension_requested',
          at: row.extension_requested_at,
          briefId: row.brief_id,
          briefName: row.briefs?.brief_name,
          supplierId: row.supplier_id,
          supplierName: row.suppliers?.supplier_name,
          briefOwnerId: row.briefs?.owner_id,
          briefCoOwners: (row.briefs?.brief_owners || []).map(bo => bo.user_id),
        });
      }

      // "{name} declined {brief}" — supplier-initiated, routed to owners.
      for (const row of declinedRes.data || []) {
        events.push({
          type: 'declined',
          at: row.declined_at,
          briefId: row.brief_id,
          briefName: row.briefs?.brief_name,
          supplierId: row.supplier_id,
          supplierName: row.suppliers?.supplier_name,
          briefOwnerId: row.briefs?.owner_id,
          briefCoOwners: (row.briefs?.brief_owners || []).map(bo => bo.user_id),
        });
      }

      // "{name} accepted {brief}" — supplier-initiated, routed to owners.
      for (const row of acceptedRes.data || []) {
        events.push({
          type: 'accepted',
          at: row.accepted_at,
          briefId: row.brief_id,
          briefName: row.briefs?.brief_name,
          supplierId: row.supplier_id,
          supplierName: row.suppliers?.supplier_name,
          briefOwnerId: row.briefs?.owner_id,
          briefCoOwners: (row.briefs?.brief_owners || []).map(bo => bo.user_id),
        });
      }

      // "{name} reached Oak Status" (eco) / "{name} earned {tier} badge".
      // Eco counts as an award only at Oak Status — skip intermediate growth
      // stages ('oak_status' is the eco tier value; see lib/ecoAward).
      for (const row of badgeRes.data || []) {
        const cat = row.badge_categories;
        const isEco = cat?.slug === 'eco';
        if (isEco && row.tier !== 'oak_status') continue;
        const tierName = !isEco && cat
          ? (row.tier === 'gold' ? cat.gold_name : row.tier === 'silver' ? cat.silver_name : cat.bronze_name)
          : null;
        events.push({
          type: 'badge_earned',
          at: row.awarded_at,
          supplierId: row.supplier_id,
          supplierName: row.suppliers?.supplier_name,
          badgeIsEco: isEco,
          badgeLabel: isEco ? 'Oak Status' : (tierName || cat?.name || 'a badge'),
        });
      }

      // "{supplier}'s compliance status changed to {New}" — supplier-level.
      // actorName present = manual override; absent = auto (system) recompute.
      for (const row of statusChangeRes.data || []) {
        events.push({
          type: 'compliance_status',
          at: row.changed_at,
          supplierId: row.supplier_id,
          supplierName: row.suppliers?.supplier_name,
          actorId: row.changed_by,
          actorName: row.internal_users?.full_name,
          newStatus: row.new_status,
        });
      }

      // "{teammate} amended {brief}" — routed to the brief's owners.
      for (const row of amendRows) {
        events.push({
          type: 'amended',
          at: row.created_at,
          actorId: row.created_by,
          actorName: adderNameById.get(row.created_by),
          briefId: row.brief_id,
          briefName: row.briefs?.brief_name,
          briefOwnerId: row.briefs?.owner_id,
          briefCoOwners: (row.briefs?.brief_owners || []).map(bo => bo.user_id),
        });
      }

      // "{supplier} asked a question on {brief}" — routed to the brief's owners.
      for (const row of questionAskedRes.data || []) {
        events.push({
          type: 'question_asked',
          at: row.created_at,
          briefId: row.brief_id,
          briefName: row.briefs?.brief_name,
          supplierName: row.suppliers?.supplier_name,
          briefOwnerId: row.briefs?.owner_id,
          briefCoOwners: (row.briefs?.brief_owners || []).map(bo => bo.user_id),
        });
      }

      // "{supplier} asked for an update on {brief}" — post-deadline chase.
      for (const row of updateReqRes.data || []) {
        events.push({
          type: 'update_requested',
          at: row.created_at,
          briefId: row.brief_id,
          briefName: row.briefs?.brief_name,
          supplierName: row.suppliers?.supplier_name,
          briefOwnerId: row.briefs?.owner_id,
          briefCoOwners: (row.briefs?.brief_owners || []).map(bo => bo.user_id),
        });
      }

      // "{teammate} sent a status update on {brief}" — holding update.
      for (const row of statusUpdateRes.data || []) {
        events.push({
          type: 'status_update',
          at: row.created_at,
          actorId: row.author_user_id,
          actorName: row.internal_users?.full_name,
          briefId: row.brief_id,
          briefName: row.briefs?.brief_name,
          briefOwnerId: row.briefs?.owner_id,
          briefCoOwners: (row.briefs?.brief_owners || []).map(bo => bo.user_id),
        });
      }

      // Scope filter — 'mine' keeps events the user performed
      // (actor/closed/sent) OR all activity on briefs they own/co-own.
      // Company-wide supplier events (joined / badge earned / compliance
      // status) have no ownership tie, so they DON'T belong in "Just Me" —
      // they only show under Everyone.
      const scoped = effectiveScope === 'everyone'
        ? events
        : events.filter(ev => {
            // Personal events (mentions / added-to-brief) are about the
            // current user by construction — always keep them.
            if (ev.forMe) return true;
            if (ev.actorId && ev.actorId === currentUserId) return true;
            if (ev.briefOwnerId === currentUserId) return true;
            if ((ev.briefCoOwners || []).includes(currentUserId)) return true;
            return false;
          });

      const sorted = scoped
        .filter(ev => ev.at)
        .sort((a, b) => new Date(b.at) - new Date(a.at))
        .slice(0, RECENT_ACTIVITY_MAX);
      setRecentActivity(sorted);
    })();
    return () => { cancelled = true; };
  }, [currentUserId, effectiveScope]);

  // Sparkline trend data — rows created in each of the last six
  // weeks, per stat. Scope-aware (briefs and responses respect the
  // 'mine' / 'everyone' toggle; suppliers are always company-wide
  // because there's one supplier base regardless of scope). One
  // round-trip per table, bucketed client-side; the 6-week window is
  // small enough that pulling rows is cheaper than six HEAD queries.
  //
  // The same effect also computes the "active a week ago" / "submitted
  // before a week ago" snapshot counts so the KPI delta can read as a
  // TRUE vs-last-week change rather than a flow rate that doesn't
  // square with the headline number.
  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    (async () => {
      const WEEKS = 6;
      const msPerWeek = 7 * 24 * 60 * 60 * 1000;
      const now = new Date();
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      start.setDate(start.getDate() - WEEKS * 7);
      const startISO = start.toISOString();
      const weekAgoMid = new Date(now); weekAgoMid.setHours(0, 0, 0, 0);
      weekAgoMid.setDate(weekAgoMid.getDate() - 7);
      const weekAgoISO = weekAgoMid.toISOString();

      // Suppliers — always company-wide.
      const supplierRes = await supabase.from('suppliers').select('created_at').gte('created_at', startISO);

      let briefRows = [];
      let responseRows = [];
      let weekAgoBriefs = 0;
      let weekAgoResponses = 0;

      if (effectiveScope === 'everyone') {
        const [b, r, wb, wr] = await Promise.all([
          supabase.from('briefs').select('created_at').gte('created_at', startISO),
          supabase.from('brief_responses').select('submitted_at').eq('is_submitted', true).gte('submitted_at', startISO),
          supabase.from('briefs').select('*', { count: 'exact', head: true })
            .eq('status', 'sent').is('archived_at', null).lt('created_at', weekAgoISO),
          supabase.from('brief_responses').select('*', { count: 'exact', head: true })
            .eq('is_submitted', true).lt('submitted_at', weekAgoISO),
        ]);
        briefRows = b.data || [];
        responseRows = r.data || [];
        weekAgoBriefs = wb.count || 0;
        weekAgoResponses = wr.count || 0;
      } else {
        // 'mine' — pull every brief the user has any stake in once,
        // filter client-side for trends + snapshot. Same query pattern
        // as the active-briefs count in the main stats effect.
        const { data: allBriefs } = await supabase
          .from('briefs')
          .select('id, created_at, status, archived_at, owner_id, brief_owners(user_id)');
        const myBriefs = (allBriefs || []).filter(b =>
          b.owner_id === currentUserId || (b.brief_owners || []).some(bo => bo.user_id === currentUserId)
        );
        const myBriefIds = myBriefs.map(b => b.id);
        briefRows = myBriefs
          .filter(b => b.created_at && new Date(b.created_at) >= start)
          .map(b => ({ created_at: b.created_at }));
        weekAgoBriefs = myBriefs.filter(b =>
          b.status === 'sent' && !b.archived_at && b.created_at && new Date(b.created_at) < weekAgoMid
        ).length;
        if (myBriefIds.length > 0) {
          const [r, wr] = await Promise.all([
            supabase
              .from('brief_responses')
              .select('submitted_at')
              .eq('is_submitted', true)
              .in('brief_id', myBriefIds)
              .gte('submitted_at', startISO),
            supabase
              .from('brief_responses')
              .select('*', { count: 'exact', head: true })
              .eq('is_submitted', true)
              .in('brief_id', myBriefIds)
              .lt('submitted_at', weekAgoISO),
          ]);
          responseRows = r.data || [];
          weekAgoResponses = wr.count || 0;
        }
      }

      if (cancelled) return;
      const bucketize = (rows, key = 'created_at') => {
        const buckets = new Array(WEEKS).fill(0);
        (rows || []).forEach(r => {
          const iso = r[key];
          if (!iso) return;
          const t = new Date(iso).getTime();
          const weeksAgo = Math.floor((now.getTime() - t) / msPerWeek);
          if (weeksAgo >= 0 && weeksAgo < WEEKS) buckets[WEEKS - 1 - weeksAgo]++;
        });
        return buckets;
      };
      setTrends({
        suppliers: bucketize(supplierRes.data),
        briefs: bucketize(briefRows),
        responses: bucketize(responseRows, 'submitted_at'),
      });
      setKpiWeekAgo({ briefs: weekAgoBriefs, responses: weekAgoResponses });
    })();
    return () => { cancelled = true; };
  }, [currentUserId, effectiveScope]);

  // Seed the My Notes textarea once the user record arrives. The
  // ref-guard means typing isn't clobbered by stray re-renders of
  // currentInternalUser (e.g. if the hook re-fetches in future).
  useEffect(() => {
    if (!currentInternalUser || notesSeededRef.current) return;
    setNotes(currentInternalUser.notes || '');
    notesSeededRef.current = true;
  }, [currentInternalUser]);

  // Scope is now owned by the sidebar ContextSelector — it persists to
  // internal_users.dashboard_scope and notifies, so `scope` above
  // (derived from currentInternalUser) updates and the data effects
  // re-run. Nothing to seed or persist here anymore.

  // Outside-click + Escape closes the Manage Widgets popover.
  useEffect(() => {
    if (!manageOpen) return;
    const onClick = (e) => {
      if (manageWrapperRef.current && !manageWrapperRef.current.contains(e.target)) setManageOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setManageOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [manageOpen]);

  // Time-of-day greeting buckets. Five bands rather than three so the
  // late-night and very-early-morning windows feel acknowledged rather
  // than getting a generic "Good evening".
  const greetingForHour = (hour) => {
    if (hour < 5)  return 'Working late';
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    if (hour < 22) return 'Good evening';
    return 'Working late';
  };
  const timeGreeting = greetingForHour(new Date().getHours());
  const firstName = currentInternalUser?.full_name?.split(' ')[0] || null;

  // Smart greeting recap. Picks the most urgent framing based on
  // what's true *right now* rather than a static template. Layered
  // priority so the recap can't claim you're "all caught up" while
  // the activity feed below shows recent events:
  //   1. Briefs due in the next 7 days — chase mode
  //   2. New responses received in the last 24h — review mode
  //   3. Briefs sent in the last 7 days — watch-for-replies mode
  //   4. Any activity in the last 24h — at least acknowledge it
  //   5. Truly empty — "all caught up"
  // Computed entirely from state already loaded for other panels so
  // there's no extra fetch.
  const buildRecap = () => {
    const dueCount = briefsDueThisWeek.length;
    if (dueCount > 0) {
      const todayMid = new Date(); todayMid.setHours(0, 0, 0, 0);
      const next = briefsDueThisWeek[0];
      const days = next?.required_response_time
        ? Math.max(0, Math.ceil((new Date(next.required_response_time).getTime() - todayMid.getTime()) / 86400000))
        : null;
      const briefWord = dueCount === 1 ? 'brief' : 'briefs';
      const urgentSpan = days === 0
        ? 'with 1 due today'
        : days === 1
          ? 'with 1 due tomorrow'
          : days != null
            ? `with 1 due in ${days} days`
            : null;
      return {
        leadCount: dueCount,
        leadWord: briefWord,
        leadTail: 'awaiting responses',
        urgent: urgentSpan,
      };
    }
    const now = Date.now();
    const last24h = now - 24 * 60 * 60 * 1000;
    const last7d = now - 7 * 24 * 60 * 60 * 1000;
    const recentResponses = recentActivity.filter(ev =>
      ev.type === 'response' && ev.at && new Date(ev.at).getTime() >= last24h
    );
    if (recentResponses.length > 0) {
      const word = recentResponses.length === 1 ? 'response' : 'responses';
      return {
        leadCount: recentResponses.length,
        leadWord: `new ${word}`,
        // Pure-information tail; no editorial sign-off ("Worth a review"
        // dropped). Turbo's presence carries the warmth — the recap stays
        // factual.
        leadTail: 'in the last 24 hours.',
      };
    }
    const recentSends = recentActivity.filter(ev =>
      ev.type === 'sent' && ev.at && new Date(ev.at).getTime() >= last7d
    );
    if (recentSends.length > 0) {
      const word = recentSends.length === 1 ? 'brief' : 'briefs';
      return {
        leadCount: recentSends.length,
        leadWord: word,
        leadTail: 'sent this week. Watch your inbox for responses.',
      };
    }
    const anyRecent = recentActivity.filter(ev =>
      ev.at && new Date(ev.at).getTime() >= last24h
    );
    if (anyRecent.length > 0) {
      const word = anyRecent.length === 1 ? 'update' : 'updates';
      return {
        leadCount: anyRecent.length,
        leadWord: word,
        leadTail: 'in the last 24 hours — see the activity feed below.',
      };
    }
    return { allClear: true };
  };
  const recap = buildRecap();

  // Attention lines for Otto's briefing bar — the cross-brief things waiting on
  // the user, ordered + coloured by severity (questions red → update requests
  // amber → overdue grey), matching the Briefs bar. Moved here from standalone
  // dashboard banners so Otto carries them.
  const dashAttention = [];
  if ((inboxZeroCounts.unansweredQuestions || 0) > 0) {
    const n = inboxZeroCounts.unansweredQuestions;
    dashAttention.push({ dot: 'red', title: `${n} supplier question${n === 1 ? '' : 's'} waiting`, body: 'A supplier is blocked from quoting until you answer.', ctaLabel: 'Review →', onCta: () => navigate('/briefs?tab=waiting') });
  }
  if ((inboxZeroCounts.updateRequests || 0) > 0) {
    const n = inboxZeroCounts.updateRequests;
    dashAttention.push({ dot: 'amber', title: `${n} supplier update request${n === 1 ? '' : 's'}`, body: 'Someone may be holding a crew while they wait.', ctaLabel: 'Respond →', onCta: () => navigate('/briefs?tab=waiting') });
  }
  if ((inboxZeroCounts.overdueBriefs || 0) > 0) {
    const n = inboxZeroCounts.overdueBriefs;
    dashAttention.push({ dot: 'grey', title: `${n} brief${n === 1 ? '' : 's'} past deadline, no decision recorded`, body: "Award or close them — nobody's waiting on a reply.", ctaLabel: 'Review →', onCta: () => navigate('/briefs?tab=overdue') });
  }
  // Feedback completion is the number the whole loop depends on — surface it
  // early (at 3+) so ratings don't end up from a self-selecting sample (§7).
  if ((feedbackDue.count || 0) >= 3) {
    const n = feedbackDue.count;
    const oldest = feedbackDue.oldestDays;
    const body = oldest != null
      ? `The oldest finished ${oldest === 0 ? 'today' : oldest === 1 ? 'yesterday' : `${oldest} days ago`}.`
      : 'Record them while the events are still fresh.';
    dashAttention.push({ dot: 'amber', title: `${n} events need feedback`, body, ctaLabel: 'Record them →', onCta: () => navigate('/briefs?tab=feedback') });
  }

  // Activity-feed relative timestamp. Same shape the supplier-portal
  // briefs use: "today", "yesterday", "N days ago" up to a week, then
  // an absolute date. Uppercased at the render site, not here.
  const fmtRelativeTime = (iso) => {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (isNaN(then)) return '';
    const diff = Date.now() - then;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days} days ago`;
    return fmtDateCompact(iso);
  };

  // Response-deadline urgency for the pinned-briefs panel. Mirrors the
  // logic on the Briefs list (src/pages/Briefs.js) — red when overdue,
  // amber when due within seven days, neutral otherwise. Only live
  // briefs (status='sent', not archived) carry urgency; closed/awarded
  // work has already moved on. Returns null when no deadline is set so
  // the row collapses instead of rendering an em-dash.
  const deadlineMeta = (brief) => {
    if (!brief.required_response_time) return null;
    const todayMid = new Date(); todayMid.setHours(0, 0, 0, 0);
    const sevenDaysOut = new Date(todayMid); sevenDaysOut.setDate(sevenDaysOut.getDate() + 7);
    const d = new Date(brief.required_response_time);
    const isLive = brief.status === 'sent' && !brief.archived_at;
    const isOverdue = isLive && d < todayMid;
    const isDueThisWeek = isLive && d >= todayMid && d <= sevenDaysOut;
    const tone = isOverdue ? 'text-[#E0290A]'
      : isDueThisWeek ? 'text-[#7A4E10]'
      : 'text-fusion-mid-grey';
    const suffix = isOverdue ? ' · Overdue'
      : isDueThisWeek ? ' · Due this week'
      : '';
    return { label: fmtDate(d), tone, suffix };
  };

  // Effective hidden-widget list — the local override wins if set,
  // otherwise read from the persisted user row. Compliance Gaps is
  // *additionally* gated by role: non-admins never see it, regardless
  // of toggle state.
  const effectiveHidden = hiddenWidgetsOverride !== null
    ? hiddenWidgetsOverride
    : (currentInternalUser?.dashboard_widgets?.hidden || []);
  const isWidgetVisible = (id) => {
    if (ADMIN_ONLY_WIDGETS.has(id) && !userIsAdmin) return false;
    return !effectiveHidden.includes(id);
  };

  const toggleWidget = useCallback(async (id) => {
    if (!currentUserId) return;
    const current = hiddenWidgetsOverride !== null
      ? hiddenWidgetsOverride
      : (currentInternalUser?.dashboard_widgets?.hidden || []);
    const next = current.includes(id) ? current.filter(x => x !== id) : [...current, id];
    setHiddenWidgetsOverride(next);
    await supabase
      .from('internal_users')
      .update({ dashboard_widgets: { hidden: next } })
      .eq('id', currentUserId);
  }, [currentUserId, hiddenWidgetsOverride, currentInternalUser]);

  // Tone-coded date for the briefs-due widget — same rules as the
  // pinned-briefs deadline display, but every row here is within
  // seven days so it's amber by default and red if it slipped to
  // today/past (which can happen if the brief was overdue when the
  // window opened — rare but covered).
  const dueDateMeta = (iso) => {
    if (!iso) return { label: '—', tone: 'text-fusion-mid-grey' };
    const todayMid = new Date(); todayMid.setHours(0, 0, 0, 0);
    const d = new Date(iso);
    const tone = d < todayMid ? 'text-[#E0290A]' : 'text-[#7A4E10]';
    return { label: fmtDate(d), tone };
  };

  // Build a 7-column calendar grid for the given month — week starts
  // Sunday to match the screenshot reference (S M T W T F S). The grid
  // pads with nulls for the leading days from the previous month so
  // the first of the month lines up under its weekday column, and the
  // numeric labels run 1..N for the visible month only. Returns the
  // grid plus a couple of derived bits the renderer needs (month
  // label, today comparison key).
  const calendarGrid = (() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    // Monday-first column order per 2026-06 dashboard corrections #1.
    // getDay() returns Sun=0..Sat=6; remap so Mon=0..Sun=6 by
    // shifting +6 mod 7. The leading-null pad then matches the
    // number of weekday columns to skip before the 1st of the month.
    const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    const monthLabel = calendarMonth.toLocaleString('en-GB', { month: 'long', year: 'numeric' });
    const now = new Date();
    const isThisMonth = now.getFullYear() === year && now.getMonth() === month;
    const todayDay = isThisMonth ? now.getDate() : null;
    return { cells, monthLabel, todayDay };
  })();

  const shiftCalendarMonth = (delta) => {
    setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };

  // Render a single widget by id. Switch keeps the call sites tidy
  // (each column just maps over its ids) without forcing every
  // widget into its own file.
  const renderWidget = (id) => {
    switch (id) {
      case 'recent_activity': {
        // Render activity events with type-specific copy + icon
        // colouring. Each event drills into the related brief or
        // supplier so the user can jump straight to context.
        // Today-event detector — compares ev.at to local midnight so any
        // event timestamped within the current calendar day reads as
        // "Today" regardless of what hour. Drives the dot ring colour
        // below.
        const isTodayEvent = (ev) => {
          if (!ev?.at) return false;
          const d = new Date(ev.at);
          const n = new Date();
          return d.getFullYear() === n.getFullYear()
            && d.getMonth() === n.getMonth()
            && d.getDate() === n.getDate();
        };
        // Milestone classifier — drives the dot FILL. Per the latest
        // annotation, ONLY awarded briefs get a filled dot; every
        // other event type stays as a hollow ring with just the
        // ring colour carrying the recency signal. Responses /
        // approvals / sent / resend etc. are all routine timeline
        // entries — filling them all in made awards lose their
        // status as the visual stand-out.
        const MILESTONE_TYPES = new Set(['award', 'mention', 'added', 'badge_earned']);
        const renderEvent = (ev, idx, arr) => {
          const goBrief = () => ev.briefId && navigate(`/briefs/${ev.briefId}`);
          const goSupplier = () => ev.supplierId && navigate(`/suppliers/${ev.supplierId}`);
          let line, onClick;
          switch (ev.type) {
            case 'sent':
              line = (
                <>
                  <strong className="text-ink font-bold">{ev.actorName || 'A teammate'}</strong>{' '}
                  {ev.actorId === currentUserId ? <span>sent</span> : <span>sent</span>}{' '}
                  <strong className="text-ink font-bold">{ev.briefName || 'a brief'}</strong>
                  {ev.count > 1 && <span className="text-fusion-mid-grey text-[11px] ml-1">to {ev.count} suppliers</span>}
                </>
              );
              onClick = goBrief;
              break;
            case 'resend':
              line = (
                <>
                  <strong className="text-ink font-bold">{ev.actorName || 'A teammate'}</strong>{' '}
                  resent <strong className="text-ink font-bold">{ev.briefName || 'a brief'}</strong>
                </>
              );
              onClick = goBrief;
              break;
            case 'response':
              line = (
                <>
                  <strong className="text-ink font-bold">{ev.supplierName || 'A supplier'}</strong>{' '}
                  submitted a response to{' '}
                  <strong className="text-ink font-bold">{ev.briefName || 'a brief'}</strong>
                </>
              );
              onClick = goBrief;
              break;
            case 'award':
              line = (
                <>
                  <strong className="text-ink font-bold">{ev.actorName || 'A teammate'}</strong>{' '}
                  awarded <strong className="text-ink font-bold">{ev.briefName || 'a brief'}</strong>{' '}
                  to <strong className="text-ink font-bold">{ev.supplierName || 'a supplier'}</strong>
                </>
              );
              onClick = goBrief;
              break;
            case 'approved':
              line = (
                <>
                  <strong className="text-ink font-bold">{ev.actorName || 'A teammate'}</strong>{' '}
                  approved a document for{' '}
                  <strong className="text-ink font-bold">{ev.supplierName || 'a supplier'}</strong>
                </>
              );
              onClick = goSupplier;
              break;
            case 'reminder':
              line = (
                <>
                  <strong className="text-ink font-bold">{ev.actorName || 'A teammate'}</strong>{' '}
                  sent a compliance reminder to{' '}
                  <strong className="text-ink font-bold">{ev.supplierName || 'a supplier'}</strong>
                </>
              );
              onClick = goSupplier;
              break;
            case 'compliance_rejected':
              line = (
                <>
                  <strong className="text-ink font-bold">{ev.actorName || 'A teammate'}</strong>{' '}
                  rejected a document for{' '}
                  <strong className="text-ink font-bold">{ev.supplierName || 'a supplier'}</strong>
                </>
              );
              onClick = goSupplier;
              break;
            case 'compliance_info':
              line = (
                <>
                  <strong className="text-ink font-bold">{ev.actorName || 'A teammate'}</strong>{' '}
                  requested more info from{' '}
                  <strong className="text-ink font-bold">{ev.supplierName || 'a supplier'}</strong>
                </>
              );
              onClick = goSupplier;
              break;
            case 'question_asked':
              line = (
                <>
                  <strong className="text-ink font-bold">{ev.supplierName || 'A supplier'}</strong>{' '}
                  asked a question on{' '}
                  <strong className="text-ink font-bold">{ev.briefName || 'a brief'}</strong>
                </>
              );
              onClick = goBrief;
              break;
            case 'update_requested':
              line = (
                <>
                  <strong className="text-ink font-bold">{ev.supplierName || 'A supplier'}</strong>{' '}
                  asked for an update on{' '}
                  <strong className="text-ink font-bold">{ev.briefName || 'a brief'}</strong>
                </>
              );
              onClick = goBrief;
              break;
            case 'status_update':
              line = (
                <>
                  <strong className="text-ink font-bold">{ev.actorName || 'A teammate'}</strong>{' '}
                  sent a status update on{' '}
                  <strong className="text-ink font-bold">{ev.briefName || 'a brief'}</strong>
                </>
              );
              onClick = goBrief;
              break;
            case 'supplier_joined':
              line = (
                <>
                  <strong className="text-ink font-bold">{ev.supplierName || 'A supplier'}</strong>{' '}
                  joined the supplier base
                </>
              );
              onClick = goSupplier;
              break;
            case 'mention':
              line = (
                <>
                  <strong className="text-ink font-bold">{ev.actorName || 'A teammate'}</strong>{' '}
                  mentioned you on{' '}
                  <strong className="text-ink font-bold">{ev.briefName || 'a brief'}</strong>
                </>
              );
              onClick = goBrief;
              break;
            case 'added':
              line = (
                <>
                  <strong className="text-ink font-bold">{ev.actorName || 'A teammate'}</strong>{' '}
                  added you to{' '}
                  <strong className="text-ink font-bold">{ev.briefName || 'a brief'}</strong>
                </>
              );
              onClick = goBrief;
              break;
            case 'extension_requested':
              line = (
                <>
                  <strong className="text-ink font-bold">{ev.supplierName || 'A supplier'}</strong>{' '}
                  requested more time on{' '}
                  <strong className="text-ink font-bold">{ev.briefName || 'a brief'}</strong>
                </>
              );
              onClick = goBrief;
              break;
            case 'extension_granted':
              line = (
                <>
                  <strong className="text-ink font-bold">{ev.actorName || 'A teammate'}</strong>{' '}
                  extended the deadline on{' '}
                  <strong className="text-ink font-bold">{ev.briefName || 'a brief'}</strong>
                  {ev.count > 1
                    ? <span className="text-fusion-mid-grey text-[11px] ml-1">for {ev.count} suppliers</span>
                    : ev.supplierName ? <span className="text-fusion-mid-grey text-[11px] ml-1">for {ev.supplierName}</span> : null}
                </>
              );
              onClick = goBrief;
              break;
            case 'declined':
              line = (
                <>
                  <strong className="text-ink font-bold">{ev.supplierName || 'A supplier'}</strong>{' '}
                  declined{' '}
                  <strong className="text-ink font-bold">{ev.briefName || 'a brief'}</strong>
                </>
              );
              onClick = goBrief;
              break;
            case 'accepted':
              line = (
                <>
                  <strong className="text-ink font-bold">{ev.supplierName || 'A supplier'}</strong>{' '}
                  accepted{' '}
                  <strong className="text-ink font-bold">{ev.briefName || 'a brief'}</strong>
                </>
              );
              onClick = goBrief;
              break;
            case 'badge_earned':
              line = (
                <>
                  <strong className="text-ink font-bold">{ev.supplierName || 'A supplier'}</strong>{' '}
                  {ev.badgeIsEco ? 'reached' : 'earned'}{' '}
                  <strong className="text-ink font-bold">{ev.badgeLabel}</strong>
                </>
              );
              onClick = goSupplier;
              break;
            case 'compliance_status': {
              const sLabel = ev.newStatus ? ev.newStatus.charAt(0).toUpperCase() + ev.newStatus.slice(1) : 'updated';
              // Semantic status set (§9) — a compliance "Green" reads in
              // success-green, never brand lime; red/amber map to danger/warning.
              const sHex = ev.newStatus === 'red' ? '#C7443E' : ev.newStatus === 'amber' ? '#C79024' : ev.newStatus === 'green' ? '#4E9A2F' : '#23271E';
              line = ev.actorName ? (
                <>
                  <strong className="text-ink font-bold">{ev.actorName}</strong>{' '}
                  set <strong className="text-ink font-bold">{ev.supplierName || 'a supplier'}</strong>'s compliance to{' '}
                  <strong style={{ color: sHex }} className="font-bold">{sLabel}</strong>
                </>
              ) : (
                <>
                  <strong className="text-ink font-bold">{ev.supplierName || 'A supplier'}</strong>'s compliance status changed to{' '}
                  <strong style={{ color: sHex }} className="font-bold">{sLabel}</strong>
                </>
              );
              onClick = goSupplier;
              break;
            }
            case 'amended':
              line = (
                <>
                  <strong className="text-ink font-bold">{ev.actorName || 'A teammate'}</strong>{' '}
                  amended <strong className="text-ink font-bold">{ev.briefName || 'a brief'}</strong>
                </>
              );
              onClick = goBrief;
              break;
            default:
              return null;
          }
          // Dot encoding (2026-06):
          //   ring colour = recency (Active Green if today, muted grey if older)
          //   fill        = milestone (filled for awards / responses /
          //                            approved; hollow for routine
          //                            sent / resend / reminder / joined)
          const isToday = isTodayEvent(ev);
          const isMilestone = MILESTONE_TYPES.has(ev.type);
          const ringHex = isToday ? '#009159' : '#51534A';
          const dotStyle = isMilestone
            ? { background: ringHex, border: `2px solid ${ringHex}` }
            : { background: 'transparent', border: `2px solid ${ringHex}` };
          // Connecting timeline lines (2026-06): a 1px muted line runs
          // through the dot column from row top to row bottom, so the
          // sequence of dots reads as a proper timeline. Two half-lines
          // per row — top half rendered unless this is the first event,
          // bottom half rendered unless this is the last. The dot sits
          // above via z-index so the line passes BEHIND it visually.
          // 5px from row left = centre of the 10px (w-2.5) dot.
          const isFirstEvent = idx === 0;
          const isLastEvent = idx === arr.length - 1;
          // gazeHover: Otto sits in the briefing bar just above this feed, so
          // hovering a row has him glance at it -- attentive to your work rather
          // than to your mouse. The handlers go on the ROW, never on him; he
          // stays decorative and pointer-events:none, which is what stops him
          // eating a click on New Brief.
          return (
            <button
              key={ev.id || `${ev.type}-${ev.at}-${idx}`}
              type="button"
              onClick={onClick}
              {...gazeHover}
              className="relative w-full grid grid-cols-[auto_1fr_auto] gap-3 items-center py-2.5 border-b border-line last:border-b-0 cursor-pointer hover:opacity-85 transition-opacity bg-transparent border-0 text-left">
              {!isFirstEvent && (
                <span
                  aria-hidden="true"
                  className="absolute top-0 w-px h-1/2 bg-line"
                  style={{ left: '4px' }} />
              )}
              {!isLastEvent && (
                <span
                  aria-hidden="true"
                  className="absolute bottom-0 w-px h-1/2 bg-line"
                  style={{ left: '4px' }} />
              )}
              <span
                aria-hidden="true"
                className="relative z-10 w-2.5 h-2.5 rounded-full flex-shrink-0 box-border"
                style={dotStyle} />
              <span className="font-['Metropolis'] text-[13px] text-fusion-mid-grey leading-snug min-w-0">{line}</span>
              <span className="font-['Metropolis'] text-[10px] tracking-[0.15em] uppercase text-[#51534A] font-bold whitespace-nowrap">
                {fmtRelativeTime(ev.at)}
              </span>
            </button>
          );
        };

        return (
          // Recent Activity is the only widget that intentionally sits
          // ON the page canvas (no card chrome) per the 2026-06 mock.
          // The timeline rings give the section its own visual weight;
          // wrapping it in a card would compete with that signal.
          <div key={id}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2.5">
                <p className="font-['Metropolis'] text-[11px] font-bold tracking-[0.2em] uppercase text-[#51534A]">Recent Activity</p>
                <span className={`font-['Metropolis'] text-[9px] tracking-[0.18em] uppercase font-bold ${userIsAdmin ? 'text-fusion-forest' : 'text-[#2E8FA1]'}`}>
                  {effectiveScope === 'everyone' ? 'EVERYONE' : 'JUST ME'}
                </span>
              </div>
              {recentActivity.length > RECENT_ACTIVITY_LIMIT && (
                <button
                  onClick={() => setActivityExpanded(v => !v)}
                  className="font-['Metropolis'] text-[11px] font-bold tracking-[0.18em] uppercase text-fusion-mid-grey hover:text-ink transition-colors bg-transparent border-0 cursor-pointer">
                  {activityExpanded ? <>Show less</> : <>View all <span className="text-fusion-forest">›</span></>}
                </button>
              )}
            </div>
            <p className="font-['Metropolis'] text-[11px] text-fusion-mid-grey leading-relaxed mb-4 pb-3.5 border-b border-line">
              {effectiveScope === 'everyone'
                ? <>What your team has been doing in the last few days. Toggle to <strong className="text-ink font-bold">Just Me</strong> to see only your activity.</>
                : <>Your activity and what's happened on your briefs in the last few days. Toggle to <strong className="text-ink font-bold">Everyone</strong> to see team-wide activity.</>}
            </p>
            {recentActivity.length === 0 ? (
              <div className="py-8 text-center">
                <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">No recent activity.</p>
                <p className="font-['Metropolis'] text-xs text-fusion-mid-grey mt-1">Get started by sending a brief or onboarding a supplier.</p>
              </div>
            ) : (
              <div>
                {/* Dot legend (build brief #8 Task B) — unifies the two dot
                    encodings so the timeline is self-explanatory: filled
                    deep-green = a milestone (award / Oak Status), hollow
                    grey = routine activity. */}
                <div className="flex items-center gap-4 mb-3">
                  <span className="inline-flex items-center gap-1.5 font-['Metropolis'] text-[10px] tracking-[0.12em] uppercase font-bold text-ink-3">
                    <span aria-hidden="true" className="w-2.5 h-2.5 rounded-full box-border" style={{ background: '#009159', border: '2px solid #009159' }} />
                    Milestone
                  </span>
                  <span className="inline-flex items-center gap-1.5 font-['Metropolis'] text-[10px] tracking-[0.12em] uppercase font-bold text-ink-3">
                    <span aria-hidden="true" className="w-2.5 h-2.5 rounded-full box-border" style={{ background: 'transparent', border: '2px solid #51534A' }} />
                    Activity
                  </span>
                </div>
                {(activityExpanded ? recentActivity : recentActivity.slice(0, RECENT_ACTIVITY_LIMIT)).map(renderEvent)}
              </div>
            )}
          </div>
        );
      }

      case 'at_risk': {
        // Briefs that are sent but not getting traction. Computed by
        // the at-risk effect above — sorted by deadline asc, capped
        // at 6 rows so the panel stays scannable.
        const fmtDeadline = (iso) => {
          if (!iso) return null;
          const todayMid = new Date(); todayMid.setHours(0, 0, 0, 0);
          const target = new Date(iso); target.setHours(0, 0, 0, 0);
          const days = Math.round((target.getTime() - todayMid.getTime()) / 86400000);
          if (days < 0) return 'Overdue';
          if (days === 0) return 'Due today';
          if (days === 1) return 'Due tomorrow';
          return `Due in ${days} days`;
        };
        return (
          <div key={id} className="bg-white border border-line rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2.5">
                <p className="font-['Metropolis'] text-[11px] font-bold tracking-[0.2em] uppercase text-[#51534A]">At Risk</p>
                {atRiskBriefs.length > 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full border bg-[#E89A2E]/[0.08] border-[#E89A2E]/30 text-[#7A4E10] font-['Metropolis'] text-[9px] tracking-[0.15em] uppercase font-bold">
                    {atRiskBriefs.length}
                  </span>
                )}
              </div>
              <button
                onClick={() => navigate('/briefs')}
                className="font-['Metropolis'] text-[11px] font-bold tracking-[0.18em] uppercase text-fusion-mid-grey hover:text-ink transition-colors bg-transparent border-0 cursor-pointer">
                All briefs <span className="text-fusion-forest">›</span>
              </button>
            </div>
            <p className="font-['Metropolis'] text-[11px] text-fusion-mid-grey leading-relaxed mb-3.5 pb-3.5 border-b border-line">
              Briefs that aren't getting traction — low or no responses with the deadline approaching. Worth chasing or reassigning before the deadline hits.
            </p>
            {atRiskBriefs.length === 0 ? (
              <div className="flex flex-col items-center justify-center px-4 pt-6 pb-3 text-center gap-3">
                <Otto pose="sleeping" size={96} ariaLabel="" />
                <p className="font-['Metropolis'] text-sm font-bold text-ink leading-snug">{DASHBOARD_EMPTY_COPY.at_risk.primary}</p>
                <p className="font-['Metropolis'] text-xs text-fusion-mid-grey leading-relaxed max-w-[280px]">{DASHBOARD_EMPTY_COPY.at_risk.sub}</p>
              </div>
            ) : (
              <div>
                {atRiskBriefs.map(b => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => navigate(`/briefs/${b.id}`)}
                    className="w-full grid grid-cols-[1fr_auto_auto] gap-3 items-center py-2.5 border-b border-line last:border-b-0 cursor-pointer hover:opacity-85 transition-opacity bg-transparent border-0 text-left">
                    {/* Amber dot dropped — every row in this panel is by
                        definition at-risk, so a per-row amber dot was
                        re-encoding the same fact the panel header
                        already states. The per-row WHY now lives on the
                        amber response-count text alone. */}
                    <span className="min-w-0">
                      <span className="flex items-center gap-1.5 flex-wrap min-w-0">
                        <span className="font-['Metropolis'] text-[13px] font-bold text-ink truncate" title={b.brief_name}>
                          {b.brief_name || 'Untitled brief'}
                        </span>
                        {/* Cross-ref chip — appears when this brief is
                            also in the Due This Week panel. Faint so it
                            reads as a hint, not a second status. */}
                        {briefsDueThisWeekIdSet.has(b.id) && (
                          <span className="font-['Metropolis'] text-[9px] font-bold tracking-[0.15em] uppercase text-[#51534A] border border-[#DADCCD]/40 rounded-full px-1.5 py-px whitespace-nowrap">
                            also due this week
                          </span>
                        )}
                      </span>
                      {(() => {
                        const m = [b.fusion_project_name, b.brief_category].filter(Boolean).join(' · ');
                        return m ? (
                          <span className="block font-['Metropolis'] text-[11px] text-fusion-mid-grey truncate">{m}</span>
                        ) : null;
                      })()}
                    </span>
                    <span className="font-['Metropolis'] text-[11px] font-bold text-[#7A4E10] whitespace-nowrap">
                      {b.respondedCount} / {b.recipientCount} responses
                    </span>
                    <span className="font-['Metropolis'] text-[10px] tracking-[0.15em] uppercase font-semibold text-fusion-mid-grey whitespace-nowrap">
                      {fmtDeadline(b.required_response_time)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      }

      case 'briefs_due_this_week': {
        // Mirrors the At Risk layout — title row + description + hairline
        // divider + body — so the two operational widgets read as a pair
        // when they sit side-by-side in the bottom grid.
        return (
          <div key={id} className="bg-white border border-line rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2.5">
                <p className="font-['Metropolis'] text-[11px] font-bold tracking-[0.2em] uppercase text-[#51534A]">Briefs Due This Week</p>
                {briefsDueThisWeek.length > 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full border bg-[#92D400]/[0.08] border-[#92D400]/30 text-fusion-forest font-['Metropolis'] text-[9px] tracking-[0.15em] uppercase font-bold">
                    {briefsDueThisWeek.length}
                  </span>
                )}
              </div>
              <button
                onClick={() => navigate('/briefs')}
                className="font-['Metropolis'] text-[11px] font-bold tracking-[0.18em] uppercase text-fusion-mid-grey hover:text-ink transition-colors bg-transparent border-0 cursor-pointer">
                {userIsAdmin ? 'All briefs' : 'Your briefs'} <span className="text-fusion-forest">›</span>
              </button>
            </div>
            <p className="font-['Metropolis'] text-[11px] text-fusion-mid-grey leading-relaxed mb-3.5 pb-3.5 border-b border-line">
              Briefs with the supplier-response deadline landing in the next 7 days. A quick chase now keeps them tracking before the clock runs out.
            </p>
            {briefsDueThisWeek.length === 0 ? (
              <div className="flex flex-col items-center justify-center px-4 pt-6 pb-3 text-center gap-3">
                <Otto pose="sleeping" size={96} ariaLabel="" />
                <p className="font-['Metropolis'] text-sm font-bold text-ink leading-snug">{DASHBOARD_EMPTY_COPY.briefs_due_this_week.primary}</p>
                <p className="font-['Metropolis'] text-xs text-fusion-mid-grey leading-relaxed max-w-[280px]">{DASHBOARD_EMPTY_COPY.briefs_due_this_week.sub}</p>
              </div>
            ) : (
              <ul className="divide-y divide-line">
                {briefsDueThisWeek.map(b => {
                  const dm = dueDateMeta(b.required_response_time);
                  return (
                    <li key={b.id}>
                      <button
                        onClick={() => navigate(`/briefs/${b.id}`)}
                        className="w-full text-left flex items-center justify-between gap-4 py-3 group">
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5 flex-wrap min-w-0">
                            <span className="font-['Metropolis'] text-sm font-semibold text-ink truncate group-hover:text-active-green transition-colors" title={b.brief_name}>
                              {b.brief_name || 'Untitled brief'}
                            </span>
                            {/* Cross-ref chip — appears when this brief is
                                also in the At Risk panel. Faint so the
                                reader sees the dual-lens hint without
                                being shouted at. */}
                            {atRiskBriefIdSet.has(b.id) && (
                              <span className="font-['Metropolis'] text-[9px] font-bold tracking-[0.15em] uppercase text-[#51534A] border border-[#DADCCD]/40 rounded-full px-1.5 py-px whitespace-nowrap">
                                also at risk
                              </span>
                            )}
                          </span>
                          {(() => {
                            const m = [b.fusion_project_name, b.brief_category].filter(Boolean).join(' · ');
                            return m ? (
                              <span className="block font-['Metropolis'] text-xs text-fusion-mid-grey truncate">{m}</span>
                            ) : null;
                          })()}
                        </span>
                        <span className={`font-['Metropolis'] text-xs font-semibold flex-shrink-0 ${dm.tone}`}>
                          {dm.label}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      }

      // Compliance Gaps was removed from the dashboard — it lives on
      // the Suppliers > Compliance Overview page so there's one source
      // of truth for admins. The widget id stays out of WIDGETS_*
      // registries so it doesn't appear in Manage Widgets either.

      case 'pinned_suppliers':
        return (
          <div key={id} className="bg-white border border-line rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="font-['Metropolis'] text-[11px] font-bold tracking-[0.2em] uppercase text-[#51534A]">Pinned Suppliers</p>
              <button
                onClick={() => navigate('/pinned')}
                className="font-['Metropolis'] text-[11px] font-bold tracking-[0.18em] uppercase text-fusion-mid-grey hover:text-ink transition-colors bg-transparent border-0 cursor-pointer">
                Edit <span className="text-fusion-forest">›</span>
              </button>
            </div>
            {pinnedSuppliers.length === 0 ? (
              <p className="font-['Metropolis'] text-sm text-fusion-mid-grey italic">No suppliers pinned yet. Open any supplier and click <span className="text-fusion-forest font-semibold">Pin</span> in the header.</p>
            ) : (
              <ul className="divide-y divide-line">
                {pinnedSuppliers.map(s => {
                  // Compose category · region · tier — but only the bits
                  // that are actually populated. A lonely em-dash reads
                  // worse than no meta line at all, so we drop the row
                  // entirely when there's nothing to say.
                  const tierLabel = s.tier
                    ? (typeof s.tier === 'number' || /^\d+$/.test(String(s.tier)) ? `Tier ${s.tier}` : s.tier)
                    : null;
                  const meta = [s.category, s.region, tierLabel].filter(Boolean).join(' · ');
                  return (
                    <li key={s.id}>
                      <button
                        onClick={() => navigate(`/suppliers/${s.id}`)}
                        className="w-full text-left flex items-start gap-3 group py-3">
                        <span className="flex-shrink-0 mt-[3px]" aria-hidden="true">
                          <StarIcon filled size={14} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block font-['Metropolis'] text-sm font-semibold text-ink truncate group-hover:text-active-green transition-colors" title={s.supplier_name}>
                            {s.supplier_name}
                          </span>
                          {meta && (
                            <span className="block font-['Metropolis'] text-xs text-fusion-mid-grey truncate">
                              {meta}
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );

      case 'pinned_briefs':
        return (
          <div key={id} className="bg-white border border-line rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="font-['Metropolis'] text-[11px] font-bold tracking-[0.2em] uppercase text-[#51534A]">Pinned Briefs</p>
              <button
                onClick={() => navigate('/pinned')}
                className="font-['Metropolis'] text-[11px] font-bold tracking-[0.18em] uppercase text-fusion-mid-grey hover:text-ink transition-colors bg-transparent border-0 cursor-pointer">
                Edit <span className="text-fusion-forest">›</span>
              </button>
            </div>
            {pinnedBriefs.length === 0 ? (
              <p className="font-['Metropolis'] text-sm text-fusion-mid-grey italic">No briefs pinned yet. Open any brief and click <span className="text-fusion-forest font-semibold">Pin</span> in the header.</p>
            ) : (
              <ul className="divide-y divide-line">
                {pinnedBriefs.map(b => {
                  const dm = deadlineMeta(b);
                  // Meta line is project · status, plus an inline
                  // "Due this week" / "Overdue" tail when the brief
                  // is live and close to its deadline. The whole
                  // line takes the deadline tone in that case so the
                  // urgency reads without a separate row.
                  const dueTail = dm?.suffix ? dm.suffix.replace(/^\s*·\s*/, '') : null;
                  const meta = [b.fusion_project_name, b.status, dueTail].filter(Boolean).join(' · ');
                  const metaTone = dueTail ? dm.tone : 'text-fusion-mid-grey';
                  return (
                    <li key={b.id}>
                      <button
                        onClick={() => navigate(`/briefs/${b.id}`)}
                        className="w-full text-left flex items-start gap-3 group py-3">
                        <span className="flex-shrink-0 mt-[3px]" aria-hidden="true">
                          <StarIcon filled size={14} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block font-['Metropolis'] text-sm font-semibold text-ink group-hover:text-active-green transition-colors" title={b.brief_name}>
                            {b.brief_name || 'Untitled brief'}
                          </span>
                          {meta && (
                            <span className={`block font-['Metropolis'] text-xs ${metaTone}`}>
                              {meta}
                            </span>
                          )}
                        </span>
                        {dm && (
                          <span className={`flex-shrink-0 font-['Metropolis'] text-xs font-semibold tracking-[0.05em] whitespace-nowrap mt-[2px] ${dm.tone}`}>
                            {dm.label}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );

      case 'calendar': {
        const onCurrentMonth = calendarGrid.todayDay !== null;
        return (
          <div key={id} className="bg-white border border-line rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <p className="font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase text-[#51534A]">Calendar</p>
                {/* "Today" jump-back link — only surfaces when the
                    user has navigated away from the current month, so
                    it stays out of the way the rest of the time. */}
                {!onCurrentMonth && (
                  <button
                    type="button"
                    onClick={() => {
                      const now = new Date();
                      setCalendarMonth(new Date(now.getFullYear(), now.getMonth(), 1));
                    }}
                    className="font-['Metropolis'] text-[10px] font-bold tracking-[1px] uppercase text-fusion-forest hover:underline">
                    ← Today
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => shiftCalendarMonth(-1)}
                  aria-label="Previous month"
                  className="text-[#51534A] hover:text-active-green transition-colors p-1">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <p className="font-['Metropolis'] text-xs font-semibold text-ink min-w-[110px] text-center">
                  {calendarGrid.monthLabel}
                </p>
                <button
                  type="button"
                  onClick={() => shiftCalendarMonth(1)}
                  aria-label="Next month"
                  className="text-[#51534A] hover:text-active-green transition-colors p-1">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {/* Monday-first per 2026-06 dashboard corrections #1.
                  Pair this with calendarGrid's (firstWeekday + 6) % 7
                  remap — they must move together or the 1st of the
                  month falls under the wrong column. */}
              {['M','T','W','T','F','S','S'].map((d, i) => (
                <div
                  key={i}
                  className="font-['Metropolis'] text-[10px] font-bold tracking-[1px] uppercase text-fusion-mid-grey text-center py-1">
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {calendarGrid.cells.map((day, i) => {
                if (day === null) return <div key={i} aria-hidden="true" />;
                const isToday = day === calendarGrid.todayDay;
                const eventsToday = calendarEvents.filter(ev => ev.day === day);
                const hasEvents = eventsToday.length > 0;
                // Weekend mute (2026-06 calendar redesign): Sat + Sun
                // columns (indices 5 + 6 in a Monday-first grid) get a
                // lower-contrast text colour so weekday focus reads
                // first. Today / event states still win — weekend
                // muting only applies on the "default" cell.
                const isWeekend = (i % 7) >= 5;
                // Calendar tooltip — keeps the weekday prefix so the hover
                // reads "Mon 8 May" not just "8 May". Bespoke format; not
                // routed through fmtDate.
                const weekdayLabel = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day)
                  .toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
                  .replace(/\bSept\b/g, 'Sep');
                // Cell tone (2026-06 calendar redesign):
                //   today   — solid Active-Green pill + white text
                //   event   — subtle dark-grey cell fill (the cell IS
                //             the indicator; no separate dot beneath).
                //             Brighter on hover to confirm interactivity.
                //   weekend — muted text, transparent bg
                //   default — off-white text, hover-tint bg
                const cellTone =
                  isToday   ? 'bg-active-green text-white font-bold' :
                  hasEvents ? 'bg-[#E6E9DA] text-ink font-bold ring-1 ring-inset ring-fusion-forest/40 hover:bg-[#dfe3d2] transition-colors' :
                  isWeekend ? 'text-fusion-mid-grey hover:bg-white transition-colors' :
                              'text-fusion-mid-grey hover:bg-white transition-colors';
                return (
                  <div
                    key={i}
                    className={`group relative aspect-square flex items-center justify-center rounded-md font-['Metropolis'] text-[11px] ${cellTone}`}>
                    {/* Number reads as 1, 2, 3 — no leading zero (2026-06
                        calendar redesign). The zero-padded format was a
                        carryover from an earlier monospace-grid design
                        and felt heavier than the mock. */}
                    <span>{day}</span>
                    {hasEvents && (
                      <div
                        role="tooltip"
                        className="hidden group-hover:block absolute bottom-[calc(100%+6px)] left-1/2 -translate-x-1/2 z-20 bg-white border border-line rounded px-2.5 py-2 text-left pointer-events-none whitespace-nowrap min-w-[160px] shadow-[0_8px_20px_rgba(0,0,0,0.4)]">
                        <div className="font-['Metropolis'] text-[9px] tracking-[0.18em] uppercase text-[#51534A] font-bold mb-1">
                          {weekdayLabel}
                        </div>
                        {eventsToday.map((ev, idx) => (
                          <div key={idx} className="flex items-center gap-1.5 mb-0.5 last:mb-0">
                            {/* Tooltip-only marker — small dot kept here
                                because the popover lists multiple events
                                and the dots help separate them. Doesn't
                                conflict with the in-cell tint treatment. */}
                            <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 bg-[#DADCCD]" />
                            <span className="font-['Metropolis'] text-[11px] text-ink font-medium">{ev.title}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Legend — small cell sample showing the brief-due tint, so
                users learn the in-cell treatment without hovering. The
                sample chip's bg + border match the cell's actual treatment
                exactly. */}
            <div className="flex items-center gap-4 mt-3.5 pt-3.5 border-t border-line">
              <span className="inline-flex items-center gap-2 font-['Metropolis'] text-[10px] tracking-[0.15em] uppercase text-fusion-mid-grey font-semibold">
                <span aria-hidden="true" className="inline-block w-3 h-3 rounded-sm bg-[#E6E9DA] ring-1 ring-inset ring-[#92D400]/70" />
                Brief due
              </span>
            </div>
          </div>
        );
      }

      case 'my_notes': {
        // Manual save complements the debounced autosave — useful
        // when the user wants an immediate write before navigating
        // away rather than waiting on the 800ms idle timer. Button is
        return (
          <div key={id} className="bg-white border border-line rounded-xl p-6 h-full flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <p className="font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase text-[#51534A]">My Notes</p>
              {/* SaveStatus indicator drives off useAutosave above —
                  occupies a reserved width so the header doesn't
                  jitter as text changes between Saving… / Saved. */}
              <SaveStatus status={notesStatus} />
            </div>
            {/* Wrap + overlay: the textarea keeps a real placeholder
                attribute (so `:placeholder-shown` can drive the
                sibling overlay's visibility) but renders the
                placeholder text as transparent. The overlay div
                replaces it with Turbo + the same copy, centred. CSS
                in turbo.css fades the overlay out the moment the
                textarea takes focus or has content. */}
            <div className="relative flex-1 flex flex-col">
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Reminders, follow-ups, anything you want to remember…"
                className="notes-area w-full flex-1 min-h-[160px] bg-white border border-line rounded-xl text-ink placeholder-transparent px-3 py-2 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] transition-colors resize-none" />
              <div
                aria-hidden="true"
                className="notes-empty-overlay absolute inset-0 flex flex-col items-center justify-center gap-2.5 pointer-events-none p-3.5 transition-opacity duration-200">
                <Otto pose="sleeping" size={56} ariaLabel="" />
                <p className="font-['Metropolis'] text-xs text-[#51534A] text-center leading-snug max-w-[200px]">
                  Reminders, follow-ups, anything you want to remember…
                </p>
              </div>
            </div>
            <p className="mt-3 font-['Metropolis'] text-[10px] text-[#51534A]">Autosaves as you type.</p>
          </div>
        );
      }

      default:
        return null;
    }
  };

  // Recent Activity is rendered inline beneath the stats (so the
  // left column matches the calendar's height), so it's excluded
  // here to avoid rendering twice in the bottom widget grid.
  // Manage Widgets — list of widgets the *current* user is allowed to
  // see (admin-only ones drop out for non-admins). Each entry tracks
  // whether it's currently visible so the popover renders a tickbox
  // state without us having to recompute per render.
  const manageableWidgets = [...WIDGETS_TOP_IDS, ...WIDGETS_LEFT_IDS, ...WIDGETS_RIGHT_IDS]
    .filter(id => !ADMIN_ONLY_WIDGETS.has(id) || userIsAdmin)
    .map(id => ({ id, label: WIDGET_LABELS[id], visible: isWidgetVisible(id) }));

  return (
    <div className="min-h-screen bg-canvas flex">

      {/* Shared Fusion sidebar (Part A5) — the dark rail on the light app. */}
      <Sidebar currentInternalUser={currentInternalUser} />

      {/* Main content */}
      <div className="flex-1 flex flex-col">

        {/* Page header (2026-06 dashboard rebuild §3): greeting block
            (Turbo + time-of-day line + meta) lives in this row's LEFT
            slot — the standalone greeting card below is gone, its
            content merged here so the top of the dashboard reads as
            one balanced row instead of stacked cards. Right slot keeps
            the scope toggle + Widgets + New Supplier + New Brief
            cluster. Divider unchanged.

            Eyebrow + route caption dropped per user feedback —
            sidebar nav already conveys "you are on Dashboard," so the
            caption was redundant scaffolding. */}
        <div className="pt-8">
          <div className="px-8 flex items-center justify-between gap-6 mb-6 flex-wrap">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              {/* Mascot removed from the greeting header — Otto has his
                  own presence elsewhere now. */}
              <div className="min-w-0">
                {/* Title/intro weighting dropped per 2026-06 annotation —
                    "less weighting/more subtle". H1 lifts from text-2xl
                    font-bold → text-xl font-medium and shifts off-white
                    to #DADCCD (fusion-grey) so the greeting reads as a
                    quiet welcome line rather than the loudest element
                    on the page. The KPI tiles + Today's Focus get to
                    carry the primary signal. */}
                {/* Greeting (§6) — title case, ink-primary, with the
                    name in Fusion green. ~28px / 700. */}
                {/* DISPLAY, not H1 — and this is the worked example of the
                    rule. "Good afternoon, Robynne" is a PHRASE, so it stays
                    sentence case; a page title that is a NAME ("Suppliers",
                    "Briefs") takes the uppercase H1. Shouting a greeting is
                    exactly what the scale's dividing line exists to prevent,
                    and a blanket sweep did it here before this was caught. */}
                <h1 className="fusion-display-sm text-ink-primary">
                  {firstName ? (
                    <>{timeGreeting}, <span className="text-fusion-black font-bold">{firstName}</span></>
                  ) : (
                    <>Welcome to <span className="text-fusion-black font-bold">Supply Pilot</span></>
                  )}
                </h1>
                {/* Just the date here — the day's recap now lives in Otto's
                    briefing bar below (single source, in his voice) rather
                    than being duplicated on this line. */}
                <p className="font-['Metropolis'] text-[14px] text-ink-secondary mt-1 leading-relaxed">
                  {fmtDate(new Date())}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0 pt-1">
              {/* The handbook. Sits FIRST in the action row and stays quiet —
                  it is the thing a new starter needs and the thing everyone
                  else has already read, so it earns a place but not weight.
                  Same pill the four per-area explainers use, so "where do I
                  read about this" has one answer everywhere. */}
              <HowItWorksButton to="/handbook" label="Handbook" />
              {/* View scope moved to the sidebar ContextSelector (below
                  the lockup) — matches Quotezilla's CLIENT VIEW slot. */}
              {/* Manage Widgets — opens a popover with a tickbox per
                  widget so users can hide ones they don't want. Sits to
                  the left of the create CTAs (less prominent than them
                  because it's a customisation action, not a workflow
                  action). Outside-click + Escape close the popover. */}
              <div ref={manageWrapperRef} className="relative">
                <button
                  onClick={() => setManageOpen(o => !o)}
                  aria-expanded={manageOpen}
                  aria-haspopup="true"
                  className="bg-surface-card rounded-[10px] border border-[#CFD2C8] text-ink-primary font-['Metropolis'] text-sm font-semibold px-[18px] h-10 hover:border-ink-secondary transition-colors flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                  Widgets
                </button>
                {manageOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 top-full mt-2 z-20 w-72 bg-white border border-line rounded-xl shadow-lg overflow-hidden">
                    <div className="px-4 py-3 border-b border-line">
                      <p className="font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase text-[#51534A]">Show on Dashboard</p>
                    </div>
                    <div className="p-2">
                      {manageableWidgets.map(w => (
                        <label
                          key={w.id}
                          className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-white cursor-pointer transition-colors">
                          <input
                            type="checkbox"
                            checked={w.visible}
                            onChange={() => toggleWidget(w.id)}
                            className="w-4 h-4 accent-active-green" />
                          <span className="font-['Metropolis'] text-sm text-ink">{w.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <AddButton
                variant="outline"
                label="New Supplier"
                onClick={() => navigate('/suppliers/new')}
              />
              <AddButton
                variant="primary"
                label="New Brief"
                onClick={() => navigate('/briefs/new')}
              />
            </div>
          </div>
          <div className="mx-8 h-px bg-[#E6E9DA]" aria-hidden="true" />
        </div>

        {/* Content — spans the full main column so the cards line up
            with the page heading and primary CTAs above. */}
        <div className="flex-1 p-8">

          {/* Otto's briefing bar — his permanent home. First login shows
              the dismissible "Hello, I'm Otto" intro; after that it carries
              the day's briefing (the recap ladder) in his voice. */}
          <OttoBriefingBar
            firstName={firstName}
            recap={recap}
            showIntro={showIntro}
            onDismissIntro={dismissIntro}
            onNavigateBriefs={() => navigate('/briefs')}
            attention={dashAttention}
          />

          {/* Greeting+calendar top row REMOVED (2026-06 dashboard rebuild
              §3). Greeting content moved into the page header above;
              Calendar now pairs with the new Today's Focus tile below
              the KPI band so the layout reads:
                  header → KPI funnel → focus+calendar → activity →
                  pinned trio → urgency pair → footer
              The greeting card chrome (linear-gradient stripe, Turbo
              tooltip) is gone — the header is information-dense enough
              that wrapping it in card chrome would add visual weight
              without adding scannable signal. */}

          {/* KPI funnel row — three tiles read left-to-right as briefs
              in flight → recipients we're still waiting on → submitted
              responses. Replaces the old Total Suppliers / Active
              Briefs / Responses lineup. Drillable; sparklines preserved;
              animations preserved. */}
          <div className="mb-8">
            {/* original-position KPI block lives below; closing the
                top section here so the IIFE renders outside the
                greeting/calendar grid. */}
              {(() => {
                const sparkSuppliers = trends.suppliers || [];
                const sparkBriefs = trends.briefs || [];
                const sparkResponses = trends.responses || [];
                // Tooltip labels for the per-point hover. Sparklines
                // bucket back from "now" so position 0 is the oldest
                // bucket, last position is the current week. Generate
                // labels off whichever array is longest (they should
                // all match WEEKS in the trends effect, but defensive
                // is cheap).
                const sparkLen = Math.max(sparkSuppliers.length, sparkBriefs.length, sparkResponses.length);
                const sparkLabels = Array.from({ length: sparkLen }, (_, i) => {
                  const weeksAgo = sparkLen - 1 - i;
                  if (weeksAgo === 0) return 'This week';
                  if (weeksAgo === 1) return 'Last week';
                  return `${weeksAgo} weeks ago`;
                });
                const ScopeTag = ({ tone, label }) => (
                  <span className={`font-['Metropolis'] text-[9px] tracking-[0.18em] uppercase font-bold ml-2 ${tone}`}>
                    {label}
                  </span>
                );
                // Delta (2026-06 dashboard corrections #7). Coloured by
                // MEANING, not direction — caller passes an explicit
                // tone so a drop isn't auto-painted red:
                //   positive (green) — outcome we want to see more of
                //   neutral  (grey)  — no change worth flagging
                //   watch    (amber) — drift worth eyeballing
                //   warn     (red)   — genuine alarm
                // Zero renders as a muted "No {suffix}" sentence so the
                // tile doesn't read as "— 0 new this week" (post-review
                // refinement — that prefix-dash + zero pairing parsed
                // as broken state).
                const Delta = ({ value, suffix, tone = 'positive' }) => {
                  if (value === 0) {
                    return (
                      <span className={`kpi-delta ${kpiAnimationDone ? 'kpi-delta--visible' : ''} font-['Metropolis'] text-xs text-[#51534A] font-semibold whitespace-nowrap`}>
                        No {suffix}
                      </span>
                    );
                  }
                  const arrow = value > 0 ? '▲' : '▼';
                  // Trend arrows use the semantic status set (§6): a
                  // positive move is success-green (NOT brand lime), a
                  // warn/watch drift is danger/warning.
                  const cls =
                    tone === 'warn'    ? 'text-danger' :
                    tone === 'watch'   ? 'text-warning' :
                    tone === 'neutral' ? 'text-ink-secondary' :
                                         'text-success';
                  return (
                    <span className={`kpi-delta ${kpiAnimationDone ? 'kpi-delta--visible' : ''} font-['Metropolis'] text-xs text-ink-secondary font-semibold whitespace-nowrap`}>
                      <span className={`${cls} font-bold`}>{arrow} {Math.abs(value)}</span>{' '}{suffix}
                    </span>
                  );
                };
                const animateSparks = statsLoaded && !kpiAnimationDone;
                // "New this week" deltas — drawn from the last bucket of
                // the 6-week trends array, which counts items by
                // created_at / submitted_at falling inside the current
                // calendar week. Mathematically guaranteed ≤ the
                // headline count, so the old "▲12 on a base of 7"
                // glitch from current-minus-snapshot can't recur.
                const briefsThisWeek    = Array.isArray(trends.briefs)    && trends.briefs.length    ? trends.briefs[trends.briefs.length - 1]    : 0;
                const responsesThisWeek = Array.isArray(trends.responses) && trends.responses.length ? trends.responses[trends.responses.length - 1] : 0;
                // KPI funnel (2026-06 dashboard rebuild §3): three
                // tiles read as a left-to-right RESPONSE funnel —
                // briefs we have in flight → recipients we're still
                // waiting on → submissions received. Total Suppliers
                // dropped: that count lives on the Suppliers page; the
                // dashboard isn't where you check it. Awarded £ also
                // not here — Reporting owns that.
                //
                // Tile 1: Active Briefs (was tile 2; promoted to lead)
                // Tile 2: Awaiting Responses (new — uses
                //          inboxZeroCounts.pendingResponses for the
                //          headline and overdueBriefs for the tail).
                //          Always company-wide because pending-response
                //          state spans the whole team's outbound briefs.
                // Tile 3: Submitted (renamed from Responses — clearer
                //          and matches the brief.)
                const overdueResp = inboxZeroCounts.overdueBriefs || 0;
                const awaitingResp = inboxZeroCounts.pendingResponses || 0;
                // Sparkline colour is semantic per tile (build brief #8
                // Task B): neutral volume metrics (Active, Submitted) read
                // mid-grey #51534A; attention (Awaiting) reads teal; and a
                // genuine downward award trend is the one place red belongs.
                const isFalling = (arr) =>
                  Array.isArray(arr) && arr.length >= 2 && arr[arr.length - 1] < arr[0];
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
                    <button onClick={() => navigate(`/briefs?status=sent${effectiveScope === 'mine' ? `&owner=${currentUserId}` : ''}`)}
                      className="text-left bg-surface-card border border-border-subtle rounded-card shadow-card p-5 focus:outline-none focus-visible:border-fusion-green hover:border-ink-tertiary transition-colors">
                      <p className="font-['Metropolis'] text-[11px] font-semibold tracking-[0.12em] uppercase text-ink-tertiary mb-2.5">
                        Active Briefs
                        <ScopeTag tone="text-info" label={effectiveScope === 'mine' ? 'MINE' : 'EVERYONE'} />
                      </p>
                      <div className="flex items-baseline justify-between gap-x-3 gap-y-1 flex-wrap mb-1">
                        <p className="kpi-value-number font-['Metropolis'] text-[30px] font-bold text-ink-primary tabular-nums leading-none">{briefsCount.display.toLocaleString('en-GB')}</p>
                        {/* "New this week" — count of briefs created in
                            the last 7 days, never exceeds the headline.
                            Tone always positive (any new brief is a
                            good outcome for the team). 2026-06
                            corrections #7. */}
                        <Delta value={briefsThisWeek} suffix="new this week" tone="positive" />
                      </div>
                      <Sparkline data={sparkBriefs} animate={animateSparks} labels={sparkLabels} color="#51534A" />
                    </button>
                    <button onClick={() => navigate('/briefs?status=sent')}
                      className="text-left bg-surface-card border border-border-subtle rounded-card shadow-card p-5 focus:outline-none focus-visible:border-fusion-green hover:border-ink-tertiary transition-colors">
                      {/* No scope tag here per 2026-06 dashboard
                          corrections #6: the pendingResponses count is
                          a single company-wide query (brief_recipients
                          joined to active briefs across the team) and
                          can't be scoped per-user without a separate
                          fetch. The brief says "drop the label rather
                          than show one that contradicts the toggle",
                          so the eyebrow stays clean and the user reads
                          it as global by inference. */}
                      <p className="font-['Metropolis'] text-[11px] font-semibold tracking-[0.12em] uppercase text-ink-tertiary mb-2.5">
                        Awaiting Responses
                      </p>
                      <div className="flex items-baseline justify-between gap-x-3 gap-y-1 flex-wrap mb-1">
                        <p className="kpi-value-number font-['Metropolis'] text-[30px] font-bold text-ink-primary tabular-nums leading-none">{awaitingResp.toLocaleString('en-GB')}</p>
                        {/* Overdue tail uses fusion-amber not the Delta
                            template — the connotation is "attention
                            required" not "trend up/down", so a delta
                            arrow would mis-frame it. */}
                        <span className={`kpi-delta ${kpiAnimationDone ? 'kpi-delta--visible' : ''} font-['Metropolis'] text-xs font-semibold whitespace-nowrap ${overdueResp > 0 ? 'text-danger' : 'text-ink-tertiary'}`}>
                          {overdueResp > 0 ? `⚠ ${overdueResp} overdue` : 'None overdue'}
                        </span>
                      </div>
                      <Sparkline data={sparkBriefs} animate={animateSparks} labels={sparkLabels} color="#2E8FA1" />
                    </button>
                    <button onClick={() => navigate(`/briefs${effectiveScope === 'mine' ? `?owner=${currentUserId}` : ''}`)}
                      className="text-left bg-surface-card border border-border-subtle rounded-card shadow-card p-5 focus:outline-none focus-visible:border-fusion-green hover:border-ink-tertiary transition-colors">
                      <p className="font-['Metropolis'] text-[11px] font-semibold tracking-[0.12em] uppercase text-ink-tertiary mb-2.5">
                        Submitted
                        <ScopeTag tone="text-info" label={effectiveScope === 'mine' ? 'MINE' : 'EVERYONE'} />
                      </p>
                      <div className="flex items-baseline justify-between gap-x-3 gap-y-1 flex-wrap mb-1">
                        <p className="kpi-value-number font-['Metropolis'] text-[30px] font-bold text-ink-primary tabular-nums leading-none">{responsesCount.display.toLocaleString('en-GB')}</p>
                        {/* "New this week" — count of responses submitted
                            in the last 7 days. Tone always positive
                            (any new submission is good progress).
                            2026-06 corrections #7. */}
                        <Delta value={responsesThisWeek} suffix="new this week" tone="positive" />
                      </div>
                      <Sparkline data={sparkResponses} animate={animateSparks} labels={sparkLabels} color="#51534A" />
                    </button>
                    {/* Awarded (MTD) — fourth KPI tile (2026-06 mock).
                        Sum of awarded_amount on briefs closed this
                        calendar month, with a % delta vs the prior
                        month. Always company-wide (the value matters
                        regardless of who owns each brief), so no
                        scope tag. Compact display: £42k / £417k —
                        kpi-value-number gets the same 38px treatment
                        as the integer tiles. */}
                    <button onClick={() => navigate('/reporting')}
                      className="text-left bg-surface-card border border-border-subtle rounded-card shadow-card p-5 focus:outline-none focus-visible:border-fusion-green hover:border-ink-tertiary transition-colors">
                      <p className="font-['Metropolis'] text-[11px] font-semibold tracking-[0.12em] uppercase text-ink-tertiary mb-2.5">
                        Awarded (MTD)
                      </p>
                      <div className="flex items-baseline justify-between gap-x-3 gap-y-1 flex-wrap mb-1">
                        <p className="kpi-value-number font-['Metropolis'] text-[30px] font-bold text-ink-primary tabular-nums leading-none">
                          {/* Compact GBP — £42k / £417k / £1.2M.
                              Inlined rather than a helper because it's
                              currently only used here; promote to a
                              lib helper if a second surface needs it. */}
                          {(() => {
                            if (awardedMtd >= 1_000_000) return `£${(awardedMtd / 1_000_000).toFixed(1)}M`;
                            if (awardedMtd >= 1_000)     return `£${Math.round(awardedMtd / 1_000)}k`;
                            return `£${Math.round(awardedMtd)}`;
                          })()}
                        </p>
                        {awardedPrevMonth > 0 ? (
                          (() => {
                            const pct = Math.round(((awardedMtd - awardedPrevMonth) / awardedPrevMonth) * 100);
                            const prevMonthLabel = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).toLocaleString('en-GB', { month: 'short' });
                            // Custom render (not Delta) so we can append
                            // the % sign — Delta doesn't know about units.
                            const arrow = pct > 0 ? '▲' : pct < 0 ? '▼' : '—';
                            // Semantic (§6): awarded up = success, down = danger.
                            const cls = pct === 0 ? 'text-ink-secondary' : pct > 0 ? 'text-success' : 'text-danger';
                            return (
                              <span className={`kpi-delta ${kpiAnimationDone ? 'kpi-delta--visible' : ''} font-['Metropolis'] text-xs text-ink-secondary font-semibold whitespace-nowrap`}>
                                <span className={`${cls} font-bold`}>{arrow} {Math.abs(pct)}%</span> vs {prevMonthLabel}
                              </span>
                            );
                          })()
                        ) : (
                          <span className="kpi-delta kpi-delta--visible font-['Metropolis'] text-xs font-semibold whitespace-nowrap text-ink-tertiary">
                            No prior month
                          </span>
                        )}
                      </div>
                      <Sparkline data={sparkResponses} animate={animateSparks} labels={sparkLabels} color={isFalling(sparkResponses) ? '#E0290A' : '#51534A'} />
                    </button>
                  </div>
                );
              })()}
          </div>

          {/* Top row: Recent Activity (wide, lg:col-span-2) + Calendar
              (narrow, lg:col-span-1), top-aligned. At Risk + Briefs Due This
              Week moved to their own row below this. Each widget respects
              Manage Widgets visibility; falls back to full width when only
              one of the two is shown. */}
          {(() => {
            // Recent Activity (wide canvas) sits next to the narrower
            // Calendar; At Risk + Briefs Due move to their own row below.
            // Top-aligned so the shorter Calendar leaves a ragged bottom
            // rather than stretching. Full width when only one is visible.
            const showActivityTop = isWidgetVisible('recent_activity');
            const showCalTop = isWidgetVisible('calendar');
            if (!showActivityTop && !showCalTop) return null;
            if (showActivityTop && showCalTop) {
              return (
                <div className="mb-8 grid grid-cols-1 lg:grid-cols-4 gap-6 items-end">
                  <div className="lg:col-span-3">{renderWidget('recent_activity')}</div>
                  <div className="lg:col-span-1">{renderWidget('calendar')}</div>
                </div>
              );
            }
            return (
              <div className="mb-8">
                {showActivityTop ? renderWidget('recent_activity') : renderWidget('calendar')}
              </div>
            );
          })()}

          {/* Inbox Zero — rare full-width acknowledgement that all
              five trigger counts are zero (no pending responses, no
              overdue briefs, no amber/red/not-set suppliers). Sits
              above the bottom widget grid; the widgets still render
              with their own empty states beneath. */}
          {/* The questions / update-requests / overdue banners moved up into
              Otto's briefing bar (attention lines) at the top of the page. */}

          {isInboxZero && <InboxZeroCard />}

          {/* Urgency row: At Risk + Briefs Due This Week, moved DOWN to sit
              below the Recent Activity + Calendar row. 2-col, scope-aware,
              top-aligned; full width when only one is visible. */}
          {(() => {
            const showAtRisk = isWidgetVisible('at_risk');
            const showDue = isWidgetVisible('briefs_due_this_week');
            const count = [showAtRisk, showDue].filter(Boolean).length;
            if (count === 0) return null;
            const colClass = count === 2 ? 'grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch' : '';
            return (
              <div className={`mb-8 ${colClass}`}>
                {showAtRisk && renderWidget('at_risk')}
                {showDue && renderWidget('briefs_due_this_week')}
              </div>
            );
          })()}

          {/* Curated row — Pinned Briefs + Pinned Suppliers + My Notes (never
              affected by the scope toggle). Also carries the all-widgets-hidden
              empty state, covering every widget id. */}
          {(() => {
            const allIds = ['recent_activity', 'calendar', 'at_risk', 'briefs_due_this_week', 'pinned_suppliers', 'pinned_briefs', 'my_notes'];
            if (allIds.every(id => !isWidgetVisible(id))) {
              return (
                <div className="bg-white border border-line rounded-xl p-12 text-center">
                  <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mb-2">All widgets are hidden.</p>
                  <p className="font-['Metropolis'] text-xs text-fusion-mid-grey">
                    Open <span className="text-fusion-forest font-semibold">Widgets</span> in the header to bring some back.
                  </p>
                </div>
              );
            }
            const showPinnedPair = isWidgetVisible('pinned_suppliers') || isWidgetVisible('pinned_briefs');
            const showNotes = isWidgetVisible('my_notes');
            if (!showPinnedPair && !showNotes) return null;
            return (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {showPinnedPair && (
                  <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${showNotes ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
                    {/* Pinned Briefs FIRST — briefs are the unit of work;
                        suppliers are referenced from a brief. */}
                    {isWidgetVisible('pinned_briefs') && renderWidget('pinned_briefs')}
                    {isWidgetVisible('pinned_suppliers') && renderWidget('pinned_suppliers')}
                  </div>
                )}
                {showNotes && (
                  <div className={showPinnedPair ? '' : 'lg:col-span-3'}>
                    {renderWidget('my_notes')}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Otto gallery — test/non-production environments only. */}
          {isTestEnv() && <OttoGallery />}

          <AppFooter />
        </div>
      </div>
    </div>
  );
}
